// src/services/limuloApi.ts
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { guardiaOspite } from '@/services/guardiaOspite';
import {
  getLimuloToken,
  peekLastSessionFailure,
  peekLimuloSession,
  rinnovaDopo401,
} from '@/lib/limulo/session';
import { verdictFor401AfterSessionAttempt } from '@/lib/limulo/errors';
import {
  LIMULO_PAGE_SIZE,
  capabilityPayloadOf,
  favoriteGamesOf,
  matchOf,
  matchResultPayloadOf,
  matchesOfParticipant,
  participantsOf,
  resultMediaAttachOf,
  standingsOf,
  unwrapData,
  unwrapItems,
  unwrapPagination,
} from '@/lib/limulo/model';
import {
  LIMULO_CHAT_PAGINA,
  messaggioInviatoOf,
  paginaChatOf,
  stanzaChatOf,
} from '@/lib/limulo/chatIncontro';
import { LIMULO_AZIONI_PATH, azioniDelMotoreOf, type LimuloAzioniDelMotore } from '@/lib/limulo/azionePersistente';
import { gameIdsOf, type LimuloMatchGameIds } from '@/lib/limulo/idDiGioco';
import {
  QUEUE_ROUTES,
  queueJoinBody,
  queueLeaveBody,
  queueListOf,
  ticketOf,
  type LimuloQueueList,
  type LimuloQueueTicket,
  type QueueJoinInput,
} from '@/lib/limulo/code';
import {
  CHALLENGE_ROUTES,
  challengeListOf,
  challengeOf,
  createChallengeBody,
  type LimuloChallengeList,
} from '@/lib/limulo/challenge';
import { partiteLiveOf } from '@/lib/limulo/partiteInCorso';
import { CHESS_TICKET_ROUTE, chessTicketBody, chessTicketRispostaOf, type ChessTicketAssente, type ChessTicketGrant } from '@/lib/chess/tavolo';
import { PRATICA_ROUTE, praticaRispostaOf, type PraticaRisposta, type SceltePratica } from '@/lib/chess/pratica';
import { BURRACO_TICKET_ROUTE, burracoTicketBody, burracoTicketGrantOf, type BurracoTicketGrant } from '@/lib/burraco/ticket';
import { BURRACO_CONDOTTA_ROUTE, burracoCondottaOf, type CondottaLetta } from '@/lib/burraco/condotta';
import { BURRACO_TAVOLI_MIEI_ROUTE, burracoTavoliMieiOf, type BurracoTavoliMiei } from '@/lib/burraco/tavoliMiei';
import {
  BURRACO_TAVOLI_ROUTE,
  burracoPostiRoute,
  creaTavoloBody,
  entraBody,
  ingressoOf,
  tavoloCreatoOf,
  type IngressoRiuscito,
  type TavoloCreato,
  type TipoTavolo,
} from '@/lib/burraco/scritture';
import {
  QCP_REST,
  qcpAcceptedOf,
  qcpInviteOf,
  qcpInvitePreviewOf,
  qcpLeaderboardOf,
  qcpMatchOf,
  qcpMeOf,
  qcpMyMatchesOf,
  qcpReplayOf,
  type LeaderboardEntry,
  type MatchReplay,
  type MatchSummary,
  type QcpInvite,
  type QcpInviteAccepted,
  type QcpInvitePreview,
  type QcpMe,
} from '@/lib/qcp/rest';
import { QCP_TICKET_ROUTE, qcpTicketBody, qcpTicketGrantOf, type QcpTicketGrant, type QcpTicketPurpose } from '@/lib/qcp/ticket';
import {
  BRIDGE_PRATICA_ROUTE,
  BRIDGE_TAVOLI_MIEI_ROUTE,
  BRIDGE_TICKET_ROUTE,
  bridgePraticaRispostaOf,
  bridgeTavoliMieiOf,
  bridgeTicketBody,
  bridgeTicketRispostaOf,
  bridgePraticaBody,
  type BridgeTavoliMiei,
  type BridgeTicketAssente,
  type BridgeTicketGrant,
  type BridgePraticaRisposta,
  type BridgePraticaScelte,
} from '@/lib/bridge/ticket';
import {
  BRIDGE_MANO_NEXT_BOARD_ROUTE,
  BRIDGE_MANO_TODAY_ROUTE,
  BRIDGE_QUOTIDIANO_NEXT_BOARD_ROUTE,
  BRIDGE_QUOTIDIANO_TODAY_ROUTE,
  bridgeManoOf,
  bridgeNextBoardOf,
  bridgeQuotidianoOf,
  type ManoDelGiorno,
  type QuotidianoDelGiorno,
} from '@/lib/bridge/manoDelGiorno';
import { walletsOf } from '@/lib/limulo/wallet';
import type { Wallet } from '@/types/qbank';
import type {
  LimuloChatMessage,
  LimuloChatPage,
  LimuloChallengeWithIdentities,
  LimuloFavoriteGames,
  LimuloGame,
  LimuloGameMatchMode,
  LimuloLogoutResult,
  LimuloMatch,
  LimuloMatchChatRoom,
  LimuloMatchResultPayload,
  LimuloMe,
  LimuloPagination,
  LimuloParticipant,
  LimuloPartiteLive,
  LimuloPlatform,
  LimuloProfile,
  LimuloRegistrationRequirements,
  LimuloRegistrationResult,
  LimuloRegistrationStatus,
  LimuloResultMediaAttach,
  LimuloTournament,
} from '@/types/limulo';

/**
 * Client del BFF Limulo (`app-qlash-api`) — la TMA e' la PRIMA superficie consumatrice
 * reale di questo backend (ADR-0078). Bersaglio SOLO staging.
 *
 * ⚠️ QUATTRO COSE CHE QUESTO CLIENT NON CONDIVIDE CON GLI ALTRI TREDICI
 *
 * 1. **L'auth non e' Logto.** Il Bearer e' un JWT INTERNO emesso dal BFF in cambio di un
 *    token esterno (`lib/limulo/auth.ts`). `src/lib/logto.ts` non viene toccato: nessuna
 *    resource nuova, nessuno scope nuovo. Se il token non c'e', l'header non si mette —
 *    un `Bearer ` vuoto produrrebbe un 401 che somiglia a un problema di permessi.
 *
 * 2. **L'envelope e' suo.** `{ok, organizationId, data, meta}` e, per le liste,
 *    `data: {items: […]}` con la `pagination` FUORI da `data` (`router.js:73-92`).
 *    `transformResponse` la srotola qui, una volta sola, cosi' le schermate vedono dati
 *    e non buste.
 *
 * 3. 🔴 **`X-AM-ORGANIZATION` SI', e su OGNI rotta — corretto il 2026-09-05 su una misura
 *    che ha smentito la scelta precedente.**
 *
 *    Qui c'era scritto: «l'organizzazione la porta il JWT interno, non mandarlo e' la
 *    scelta piu' sicura». **Falso**, e il costo e' stato una sezione che si apriva e poi
 *    moriva sul dettaglio, con un messaggio che nessuno sapeva leggere.
 *
 *    Misurato su `qlash-limulo@origin/staging`: il BFF ricava l'organizzazione da
 *    `assertUserContext(req)` (`router.js:177-181`), cioe' da cio' che ha messo il
 *    middleware d'auth dell'HOST — e quel middleware la legge **dall'header**. Senza
 *    header, e fuori da una allowlist di sei path (`/auth/me`, `/billing`, …), risponde
 *    **400 «X-AM-ORGANIZATION header is required»** (`middlewares/auth.ts:272-283`).
 *
 *    ⚠️ Il rischio dell'header sbagliato resta vero e non e' stato tolto: un id che non
 *    combacia da' 403 «does not match the active membership». Ma e' un rischio di
 *    VALORE, mentre non mandarlo era un guasto CERTO — e l'errore che si sceglie e'
 *    diverso: 403 dice «org sbagliata», il 400 non diceva niente di utilizzabile.
 *
 *    Si manda solo se c'e' un valore: un header vuoto sarebbe la terza forma di guasto.
 *
 * 4. **Le scritture ci sono dal 2026-09-02 — e la guardia sul retry le copre gia'.**
 *    Fase 1-2 erano di sola lettura; la fase 3 aggiunge iscrizione, referto, prova
 *    allegata, check-in, forfeit, profili e logout (spec:
 *    `docs/analysis/2026-09-02-limulo-collasso-laravel.md` §2). Il `retryCondition` qui
 *    sotto e' scritto sul METODO e non sui nomi delle rotte, quindi nessuna POST viene
 *    mai ritentata: un'iscrizione ripetuta da sola sarebbe una seconda iscrizione.
 *    ⚠️ Sul referto resta il finding aperto lato server (il body non viene spogliato di
 *    `providerResult`): il client non lo nomina, e il gate lo verifica.
 *
 * ⚠️ E UNA COSA CHE VALE PER TUTTE E SETTE: se l'app `qlash-api` non e' attiva
 * sull'organizzazione, ognuna risponde **403 anche con un token valido**. Non e' un
 * guasto da inseguire — e' un interruttore su un backoffice — e le schermate lo dicono
 * invece di mostrare un vuoto.
 * Notare la forma condizionale: qui c'era «con l'app spenta OGGI…», che e' un fatto sul
 * mondo e come tale e' invecchiato in silenzio. Lo stato corrente non si legge dal
 * codice: sta nel KB, `services/qlash-limulo/identita.md`.
 */
/**
 * Il 401 che discende da una NOSTRA scadenza non e' un problema di identita'.
 *
 * 🔴 Percorso misurato il 2026-09-06: l'exchange di sessione scade ⇒ nessun token ⇒ nessun
 * header ⇒ **il server risponde 401 e ha ragione** — quella richiesta non portava credenziali.
 * Ma la schermata leggeva quel 401 come «Identita' non valida — serve una nuova
 * autenticazione», cioe' mandava l'utente a rifare il login (dentro Telegram: il ponte col
 * browser di sistema) per una richiesta che bastava riprovare.
 *
 * ⇒ Qui si ricongiunge cio' che sa `session.ts` (ho rinunciato io) con cio' che vede il
 * client (401). La regola e' pura e sta in `errors.ts`; questo e' solo il punto in cui i due
 * pezzi si incontrano. ⛔ Vale SOLO per la scadenza: un 401 dopo un rifiuto vero resta un
 * rifiuto vero, o si nasconderebbe l'unico caso in cui «rifai l'accesso» e' giusto.
 */
const spiegaIl401 = (
  error: { status?: number | string; data?: unknown } | undefined,
): typeof error => {
  if (!error) return error;
  // ⚠️ `peekLimuloSession()` non tocca la rete: e' la sessione com'e' a riposo. Serve perche'
  // «non abbiamo mandato l'header» spiega un 401 solo se davvero non abbiamo una sessione.
  const causa = verdictFor401AfterSessionAttempt(
    error.status,
    peekLastSessionFailure(),
    peekLimuloSession() !== null,
  );
  if (causa !== 'timeout') return error;
  return { ...error, status: 'TIMEOUT_ERROR' };
};

/**
 * Il client vero, con la coda di ritentativi.
 *
 * ⚠️ Estratto qui, e non lasciato inline, per una ragione di ORDINE: la riscrittura del 401
 * causato dalla nostra scadenza deve avvenire **dopo** i ritentativi. Se avvenisse prima,
 * `retryCondition` vedrebbe `TIMEOUT_ERROR` e **ritenterebbe un 401** — che questo file
 * vieta espressamente due commenti piu' su, e per un buon motivo.
 */
const limuloBaseQuery = retry(
    fetchBaseQuery({
      baseUrl: env.limuloApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getLimuloToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        // Vedi la nota 3: l'host legge l'organizzazione da QUI, non dal JWT interno.
        // Solo se c'e' un valore — `VITE_LIMULO_ORG_ID` non e' dichiarata in ogni
        // profilo, e un header vuoto e' peggio di un header assente.
        if (env.limuloOrgId) headers.set('X-AM-ORGANIZATION', env.limuloOrgId);
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    {
      /**
       * Si ritenta solo cio' che puo' cambiare da solo: rete caduta, timeout, 5xx.
       *
       * 401, 403 e **424** non si ritentano MAI (qui, nel `retry`). Il 401 con la sessione
       * presente ha un percorso SUO e sopra a questo, in `limuloComposedBaseQuery`: UN rinnovo
       * forzato e al piu' UNA ripetizione — non e' un ritentativo, e non lo decide questa funzione.
       * Ed e' il 424 il caso che vale la pena
       * spiegare: significa «nessun provider serve questa capability». Riprovare fra un
       * secondo darebbe lo stesso 424, perche' cio' che manca e' una configurazione
       * dell'organizzazione, non un pacchetto perso. Ritentarlo trasformerebbe una
       * risposta onesta e istantanea in tre secondi di rotella seguiti dalla stessa
       * risposta.
       */
      retryCondition: (error, args, { attempt }) => {
        const method =
          typeof args === 'object' && args !== null && 'method' in args
            ? (args as { method?: string }).method
            : undefined;
        if (method !== undefined && method !== 'GET') return false;
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  );

/**
 * Il Bearer che la richiesta appena fallita portava DAVVERO, letto dalla `Request` che
 * `fetchBaseQuery` rimette in `meta` (anche sugli errori, e `retry` la conserva). `null` quando
 * non c'e' un header `Authorization: Bearer …`: e' il 401 «senza header», che qui non si tocca.
 * ⛔ Il valore serve a confrontare e non va mai in un log ne' in un testo di errore.
 */
const tokenMandatoDa = (result: { meta?: unknown }): string | null => {
  const richiesta = (result.meta as { request?: Request } | undefined)?.request;
  const header = richiesta?.headers.get('Authorization') ?? '';
  return header.startsWith('Bearer ') && header.length > 'Bearer '.length
    ? header.slice('Bearer '.length)
    : null;
};

/** Il `limit` massimo che il motore accetta su `/v1/tournaments/mine` (`PAGINA_MASSIMA`). */
export const LIMULO_MINE_LIMIT_MAX = 100;

/**
 * Il baseQuery di Limulo COMPLETO — ritentativi piu' la riscrittura del 401 — nell'ordine che
 * la nota qui sopra impone.
 *
 * ⚠️ Esportato dal 2026-09-15, e l'export e' la parte che vale la pena motivare: `questApi`
 * instrada **un solo endpoint** (`getMissions`) su Limulo, e deve farlo con QUESTA catena e non
 * con una seconda scritta li'. Una copia divergerebbe al primo cambiamento — e divergerebbe in
 * silenzio, perche' una catena di ritentativi sbagliata non produce un errore: produce una
 * richiesta in piu' o un 401 mandato a rifare il login.
 * ⛔ Chi lo importa NON eredita niente altro di `limuloApi`: ne' i tag, ne' le politiche di
 * cache, ne' `transformResponse`. La busta la srotola il chiamante (`unwrapData`), com'e' giusto:
 * qui non si sa quale forma abbia chiesto.
 */
export const limuloComposedBaseQuery: typeof limuloBaseQuery = guardiaOspite('limulo', async (args, api, extraOptions) => {
  let result = await limuloBaseQuery(args, api, extraOptions);
  // 🔴 Una sessione REVOCATA dal server dichiara ancora ~27 giorni di validita' IN LOCALE, quindi
  // `getLimuloToken` non la rinnova mai e ogni richiesta torna 401. UN rinnovo forzato, UNA
  // ripetizione, mai un secondo giro: un 401 dopo la ripetizione e' l'esito, e si mostra.
  // ⛔ Solo se la richiesta PORTAVA `Authorization`: un 401 senza header e' un'altra storia, e la
  // spiega `spiegaIl401` qui sotto, com'e' oggi.
  const mandato = result.error?.status === 401 ? tokenMandatoDa(result) : null;
  if (mandato !== null) {
    // `transitorio` = il refresh non ha dato un verdetto: la sessione resta, non si ripete.
    if ((await rinnovaDopo401(mandato)) !== 'transitorio') {
      result = await limuloBaseQuery(args, api, extraOptions);
    }
  }
  // `spiegaIl401` e' dichiarata sulla forma minima che le serve (`{status?, data?}`) e non su
  // `FetchBaseQueryError`: e' cio' che tiene `lib/limulo/errors.ts` esercitabile senza importare
  // RTK. La restrizione la rimette qui chi consegna il valore a RTK — un punto solo.
  return result.error
    ? { ...result, error: spiegaIl401(result.error) as FetchBaseQueryError }
    : result;
});

/**
 * Il tag della cache del SALDO letto dalla facciata. Esportato per la stessa ragione di
 * `QBANK_WALLET_TAG` (`qbankApi.ts`): chi invalida (`questApi`, sul claim riuscito) e chi fornisce
 * (`getLimuloMyWallet`) devono nominare la STESSA cosa, e un refuso da una parte sola non da' errore:
 * da' un saldo che non si aggiorna.
 */
export const LIMULO_WALLET_TAG = 'LimuloWallet' as const;

/**
 * Una moneta del catalogo della facciata, ridotta a cio' che serve a dare un'etichetta a un
 * `currencyId`: l'identita' e il `code` da mostrare.
 */
export interface LimuloWalletCurrency {
  currencyId: string;
  code: string;
}

export const limuloApi = createApi({
  reducerPath: 'limuloApi',
  /**
   * ⛔ La riscrittura sta QUI, dopo i ritentativi, non dentro `fetchBaseQuery`: v. la nota
   * su `limuloBaseQuery`.
   */
  baseQuery: limuloComposedBaseQuery,
  /**
   * Poll-based PER COSTRUZIONE: il realtime di Limulo e' un guscio (le rotte `/stream`
   * mandano ready + heartbeat e nessun evento di dominio). Non c'e' un push da aspettare,
   * quindi la freschezza la danno il rientro sull'app e la riconnessione.
   *
   * 🔴 **E FINO AL 2026-09-24 LA DAVANO SOLO LORO — e non bastava.** In una WebView di Telegram
   * `refetchOnFocus` scatta di rado, quindi una schermata aperta non si rileggeva mai (reperto
   * di Luca dopo un torneo vero: il conteggio iscritti fermo, e il ritiro offerto a finale
   * giocata). ⇒ Il polling c'e', ma **per rotta, non qui**: il ritmo dipende da cose che solo la
   * schermata sa (il torneo e' terminale? e' la prima pagina?). Ritmi e ragioni in
   * `lib/limulo/polling.ts`, cablaggio in `hooks/useLimuloPolling.ts`.
   * ⛔ Non cercare un ritmo unico per tutta la sezione (un avvolgitore degli hook, un default
   * comune): rileggerebbe anche i tornei finiti, le pagine oltre la prima e il catalogo giochi.
   */
  keepUnusedDataFor: 60,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: [
    'LimuloTournament',
    'LimuloRegistration',
    'LimuloMatch',
    'LimuloGame',
    'LimuloProfile',
    'LimuloMe',
    'LimuloQueue',
    'LimuloChat',
    'LimuloAzioni',
    'LimuloChallenge',
    LIMULO_WALLET_TAG,
  ],
  endpoints: (builder) => ({
    /**
     * `GET /v1/tournaments` (`router.js:1733-1751`) — lista pubblica, paginata
     * (`page`/`limit`, limite massimo 100 lato server).
     */
    getLimuloTournaments: builder.query<
      { items: LimuloTournament[]; pagination: LimuloPagination | null; lastPageCount: number },
      { page?: number; limit?: number; gameId?: string } | void
    >({
      /**
       * `gameId` (I-342, 04/10): la lista del gioco selezionato, filtrata dal SERVER (`?gameId=`, un valore solo:
       * `filtriDellaLista`, `tornei.js:802-804` @ `qlash-limulo` `5baeedf`). Chi non lo passa chiede tutti i giochi.
       * Il `gameId` e' quello DELL'ORGANIZZAZIONE, ricavato dal catalogo (`lib/limulo/giocoSelezionato.ts`).
       */
      query: (args) => ({
        url: '/v1/tournaments',
        params: {
          page: args?.page ?? 1,
          limit: args?.limit ?? LIMULO_PAGE_SIZE,
          ...(args?.gameId ? { gameId: args.gameId } : {}),
        },
      }),
      /**
       * ⚠️ **Adesso esce anche la `pagination`**, che sta FUORI da `data` (`router.js:84`)
       * ed era l'unica busta di questo bordo che nessuno leggeva. `unwrapPagination` portava
       * scritto «nessun consumatore oggi, e' una capacita' in attesa di una superficie»:
       * la superficie e' arrivata il 2026-09-20 con la lista tornei (reference A1, I-92).
       */
      transformResponse: (response: unknown) => {
        const items = unwrapItems<LimuloTournament>(response);
        return {
          items,
          pagination: unwrapPagination(response),
          /**
           * 🔑 **QUANTE RIGHE HA PORTATO QUESTA PAGINA — e si conserva perche' dopo il
           * `merge` NON e' piu' ricavabile.** Serve a `hasNextPage` per il ramo in cui il
           * server non dichiara `totalPages`: li' l'unico indizio onesto e' «l'ultima pagina
           * era piena».
           * ⚠️ Non si deduce da `items.length` della lista accumulata (sarebbe il totale) ne'
           * da `pagination.limit` (che e' il limite CHIESTO, non le righe RICEVUTE — e i due
           * coincidono esattamente nel caso in cui la domanda non serve).
           */
          lastPageCount: items.length,
        };
      },
      /**
       * 🔴 **UNA SOLA VOCE DI CACHE PER TUTTE LE PAGINE**, altrimenti «pagina 2» sarebbe una
       * lista NUOVA e la schermata lampeggerebbe da sei righe a sei righe diverse.
       * ⚠️ Con questa riga l'argomento non entra piu' nella chiave, quindi `forceRefetch`
       * diventa obbligatorio: senza, RTK Query servirebbe la cache e la pagina 2 non
       * partirebbe mai — silenziosamente, perche' una richiesta non fatta non e' un errore.
       */
      /*
       * 🔴 **E UNA VOCE PER GIOCO (I-342).** La chiave e' il nome dell'endpoint piu' il `gameId`: con la chiave di prima
       * (il nome e basta) la lista di Clash Royale e quella del backgammon sarebbero finite nella STESSA voce, e il
       * `merge` qui sotto avrebbe accodato le pagine di un gioco a quelle dell'altro. Chi non passa `gameId` resta sulla
       * voce di prima (il nome nudo). Le pagine si accodano DENTRO la voce del gioco, come prima.
       * ⚠️ Ogni voce fornisce lo STESSO tag (`LimuloTournament`): un'invalidazione le rinfresca tutte.
       */
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const gameId = queryArgs && typeof queryArgs === 'object' ? queryArgs.gameId : undefined;
        return gameId ? `${endpointName}(${gameId})` : endpointName;
      },
      merge: (cache, arrivata, { arg }) => {
        const page = (arg && typeof arg === 'object' ? arg.page : undefined) ?? 1;
        // La pagina 1 SOSTITUISCE: e' cio' che arriva da un `refetchOnFocus` o da
        // un'invalidazione, e accodarla duplicherebbe l'intera lista.
        if (page <= 1) {
          cache.items = arrivata.items;
          cache.pagination = arrivata.pagination;
          cache.lastPageCount = arrivata.lastPageCount;
          return;
        }
        /**
         * ⚠️ **Dedup per `id`, e non e' paranoia**: fra una pagina e l'altra il server puo'
         * aver ricevuto un torneo nuovo, e allora la riga in coda alla pagina 1 scivola in
         * testa alla 2 e arriverebbe due volte. Una riga doppia in un elenco di tornei non
         * da' nessun errore: da' un elenco che mente sul proprio numero.
         */
        const visti = new Set(cache.items.map((t) => t.id));
        cache.items.push(...arrivata.items.filter((t) => !visti.has(t.id)));
        cache.pagination = arrivata.pagination;
        // ⚠️ Il conteggio e' quello che il SERVER ha mandato, **prima** del dedup: una riga
        // scartata perche' gia' vista non dice che la pagina fosse corta, dice che si e'
        // spostata. Contare dopo il dedup farebbe finire la paginazione in anticipo.
        cache.lastPageCount = arrivata.lastPageCount;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const p = (a: typeof currentArg) => (a && typeof a === 'object' ? a.page : undefined) ?? 1;
        return p(currentArg) !== p(previousArg);
      },
      providesTags: ['LimuloTournament'],
    }),

    /**
     * `GET /v1/tournaments/mine` (`router.js:1753-1777`) — i tornei del chiamante.
     * L'identita' e' il `userId` risolto dal token: la rotta non prende un utente, e non
     * esiste modo di chiedere i tornei di un altro.
     */
    /**
     * `GET /v1/tournaments/mine?limit=100` — i tornei del chiamante, **col totale**: per l'azione
     * persistente (`AzionePersistente`), che deve sapere quanti tornei NON ha visto. Il motore
     * rende 25 righe di default e al massimo `PAGINA_MASSIMA = 100` (`tornei.js:64` @
     * `qlash-limulo@origin/staging` `4ef5369`), ordinate per `createdAt` decrescente; il totale e' sullo
     * stesso filtro (`tornei.js` `torneiDi`). ⚠️ Endpoint separato da `getMyLimuloTournaments` apposta:
     * quello serve «Le mie iscrizioni» e non deve cambiare forma.
     */
    getMyLimuloTournamentsConTotale: builder.query<{ items: LimuloTournament[]; total: number | null }, void>({
      query: () => ({ url: '/v1/tournaments/mine', params: { limit: LIMULO_MINE_LIMIT_MAX } }),
      transformResponse: (response: unknown) => {
        const items = unwrapItems<LimuloTournament>(response);
        const dati = unwrapData<unknown>(response);
        const grezzo =
          unwrapPagination(response)?.total ??
          (dati !== null && typeof dati === 'object' ? (dati as Record<string, unknown>).total : null);
        return { items, total: typeof grezzo === 'number' && Number.isFinite(grezzo) ? grezzo : null };
      },
      providesTags: ['LimuloTournament'],
    }),

    getMyLimuloTournaments: builder.query<LimuloTournament[], { gameId?: string } | void>({
      /*
       * `gameId` (I-346): le mie del gioco della sezione, filtrate dal SERVER — `/mine` prende «gli stessi filtri della lista
       * pubblica» (`qlash-limulo` `app-qtournaments/app/superficie/capability/tornei.js:1099-1103` @ `5baeedf`; dal vivo
       * `?gameId=100`: 18 → 8, consegna-tornei-orcb 2026-10-04 13:27:11Z). Senza argomento la voce di cache resta quella di
       * sempre (la pagina `/limulo/mine`, le code, il backgammon): un argomento diverso apre una voce sua.
       */
      query: (arg) =>
        arg && arg.gameId ? { url: '/v1/tournaments/mine', params: { gameId: arg.gameId } } : '/v1/tournaments/mine',
      transformResponse: (response: unknown) => unwrapItems<LimuloTournament>(response),
      providesTags: ['LimuloTournament'],
    }),

    /**
     * `GET /v1/me/tournaments/actions` (kb#275, ADR-0167; `router.js:2151-2154` @ `qlash-limulo@7e0eae2`)
     * — le azioni in sospeso del chiamante in TUTTI i suoi tornei vivi, decise e ordinate dal motore.
     * Nessun parametro: l'identita' e' quella del token. La legge solo la barra, e solo sotto
     * `VITE_FEATURE_TOURNAMENT_ACTIONS_ENABLED` (`AzionePersistente`).
     *
     * ⚠️ I tag sono quelli che le letture di prima davano alla barra (`mine`, iscrizioni, incontri): cosi'
     * gli stessi gesti la fanno rileggere subito — un check-in, un referto, un'iscrizione, la partita
     * trovata in coda (`CodeTorneiContext` invalida `LimuloTournament`). `LimuloAzioni` e' il suo, per chi
     * deve rileggere solo lei.
     */
    getLimuloAzioni: builder.query<LimuloAzioniDelMotore | null, void>({
      query: () => LIMULO_AZIONI_PATH,
      transformResponse: (response: unknown) => azioniDelMotoreOf(response),
      providesTags: (result) => [
        'LimuloAzioni' as const,
        'LimuloTournament' as const,
        'LimuloMatch' as const,
        ...(result?.items ?? []).flatMap((v) => [
          { type: 'LimuloRegistration' as const, id: v.tournamentId },
          ...(v.matchId === null ? [] : [{ type: 'LimuloMatch' as const, id: v.matchId }]),
        ]),
      ],
    }),

    /**
     * `GET /v1/tournaments/:id?includeParticipants=true` — **gli iscritti**.
     *
     * 🔴 **NON E' UNA ROTTA SUA: e' la scheda torneo con un flag.** `buildTournamentReadPayload`
     * (`router.js:169-184` @ `qlash-limulo@origin/staging` = `ca8719b`) legge
     * `includeParticipants` (o `include_participants`) e lo passa al provider; la risposta e'
     * lo stesso torneo, con `participants` popolato.
     *
     * ⚠️ **E allora perche' una query SEPARATA invece di un parametro su `getLimuloTournament`?**
     * Perche' l'argomento entra nella chiave di cache: aggiungerlo li' spaccherebbe in due la
     * voce del torneo — una col flag e una senza — e la scheda si ritroverebbe a fare **due**
     * letture dello stesso torneo, con due esiti che possono divergere. Cosi' invece il tab
     * Iscritti degrada **da solo**, che e' la regola di questa sezione: un errore su una terza
     * parte non spegne la scheda.
     *
     * ⚠️ `participants` e' `undefined` quando il provider non lo manda, e `participantsOf`
     * risponde `[]` — che chi rende deve distinguere da «nessun iscritto». Il tab lo fa.
     */
    getLimuloTournamentParticipants: builder.query<LimuloParticipant[], string>({
      query: (id) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}`,
        params: { includeParticipants: true },
      }),
      transformResponse: (response: unknown) =>
        participantsOf((unwrapData<{ participants?: unknown }>(response) ?? {}).participants),
      providesTags: (_r, _e, id) => [{ type: 'LimuloTournament' as const, id }],
    }),

    /**
     * `GET /v1/me/tournaments/history` (`router.js:2087-2108`) — **lo storico**.
     *
     * 🔴 **NESSUNO L'HA MAI CHIAMATA** prima del 2026-09-20: la rotta esiste da prima di questo
     * giro ed era senza consumatori.
     *
     * ⚠️ **E passa dallo STESSO sanitizer della lista** (`sanitizeTournamentList`), che e' un
     * letterale a chiavi fisse ⇒ **`party` cade per intero**. Il motore ce lo mette
     * (`storicoDi` aggiunge la parte alla riga), il bordo lo butta. ⇒ dallo storico **non
     * arriva il piazzamento**, e nemmeno `entryOrder` o lo stato della propria iscrizione: la
     * riga sa **quali** tornei hai giocato e nient'altro.
     * 📌 Il catalogo del motore descrive questa capability come «i tornei conclusi in cui ha
     * giocato, **con la sua parte e il suo piazzamento**» — e **promette piu' di quanto il
     * codice mandi**. ⭐ *Una descrizione e' un'intenzione, la funzione e' il contratto.*
     *
     * ⚠️ E l'identita' **non si passa**: il resolver ricava l'attore dal contesto, e mandare un
     * `userId` lascerebbe credere a chi legge la rotta che si possa scegliere di chi vedere lo
     * storico. Lo storico altrui e' una capability sua, con un cancello suo.
     */
    getLimuloTournamentHistory: builder.query<LimuloTournament[], void>({
      query: () => '/v1/me/tournaments/history',
      transformResponse: (response: unknown) => unwrapItems<LimuloTournament>(response),
      providesTags: ['LimuloTournament'],
    }),

    /** `GET /v1/tournaments/:id` (`router.js:2086-2101`). */
    getLimuloTournament: builder.query<LimuloTournament | null, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}`,
      transformResponse: (response: unknown) => unwrapData<LimuloTournament>(response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloTournament' as const, id }],
    }),

    /** `GET /v1/tournaments/:id/registration` — stato d'iscrizione del chiamante. */
    getLimuloRegistration: builder.query<LimuloRegistrationStatus | null, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}/registration`,
      transformResponse: (response: unknown) => unwrapData<LimuloRegistrationStatus>(response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloRegistration' as const, id }],
    }),

    /**
     * `GET /v1/tournaments/:id/registration-requirements` — cosa serve per iscriversi.
     * In fase 1 si LEGGE soltanto: l'iscrizione vera e' fase 2.
     */
    getLimuloRegistrationRequirements: builder.query<LimuloRegistrationRequirements | null, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}/registration-requirements`,
      transformResponse: (response: unknown) =>
        unwrapData<LimuloRegistrationRequirements>(response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloRegistration' as const, id }],
    }),

    /**
     * `GET /v1/tournaments/:id/standings` — ⚠️ L'UNICA delle sette senza facciata: passa da
     * `handleCapabilityJson` (`router.js:1847-1853`) e restituisce la forma GREZZA del
     * provider. `standingsRows` la normalizza in modo difensivo senza scartare niente, e
     * ogni riga si porta dietro l'originale. Il contratto qui non e' scritto da nessuna
     * parte: ADR-0020 dice che diventera' quello che noi eserciteremo — motivo in piu' per
     * non fingere una forma che il server non ha promesso.
     */
    getLimuloStandings: builder.query<ReturnType<typeof standingsOf>, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}/standings`,
      // ⚠️ NON solo le righe. La risposta porta anche `final` e `unavailable`
      // (`classifica.js:185-201`), e buttarli qui significherebbe mostrare `items: []`
      // come «nessun risultato» quando il server ha detto «non disponibile, ed ecco il
      // motivo» — cioe' il finto vuoto che questa sezione esiste per non produrre.
      transformResponse: (response: unknown) => standingsOf(unwrapData(response) ?? response),
      providesTags: (_r, _e, id) => [{ type: 'LimuloTournament' as const, id }],
    }),

    /**
     * `GET /v1/wallet/currencies` — il catalogo delle monete della facciata (contratto: `PUBLIC_API_CONTRACT.md`,
     * sezione wallet; capability `qbank:coins.get`). Serve a UNA cosa: dalla 1.94.0 di qTournaments (limulo#192,
     * ADR-0187) la chiave di `premi[].ricompense` dei tornei NUOVI e' il `currencyId` (GUID) della moneta, e la
     * risposta pubblica del torneo non porta un'etichetta.
     * Busta standard, `data.items[]` = `{currencyId, name, code, model, isDefault, isEnable}`, una pagina sola.
     * ⚠️ L'identita' di una moneta e' `currencyId`; `code` e `name` sono mutabili (una rinomina li cambia senza
     * toccare i tornei): si indicizza per `currencyId` e si mostra il `code`. Le voci senza i due campi si
     * scartano qui, una per una: il catalogo e' un aiuto all'etichetta, non una fonte da cui far cadere la schermata.
     * Cache lunga (un giorno): il catalogo cambia di rado, e un errore NON resta in cache come risposta valida.
     */
    getLimuloWalletCurrencies: builder.query<LimuloWalletCurrency[], void>({
      query: () => '/v1/wallet/currencies',
      transformResponse: (response: unknown) => {
        const items = unwrapData<{ items?: unknown }>(response)?.items;
        if (!Array.isArray(items)) return [];
        return items.flatMap((voce): LimuloWalletCurrency[] => {
          const { currencyId, code } = (voce ?? {}) as Record<string, unknown>;
          return typeof currencyId === 'string' && currencyId !== '' && typeof code === 'string' && code !== ''
            ? [{ currencyId, code }]
            : [];
        });
      },
      keepUnusedDataFor: 86_400,
    }),

    /** `GET /v1/me` (`router.js:825-845`) — utente + membership. Nessuna capability dietro. */
    getLimuloMe: builder.query<LimuloMe | null, void>({
      query: () => '/v1/me',
      transformResponse: (response: unknown) => unwrapData<LimuloMe>(response),
      providesTags: ['LimuloMe'],
    }),

    /**
     * `PUT /v1/me/nickname` — la scelta del nome pubblico (ADR-0125, Limulo #48/#49,
     * entrambe mergiate; contratto riportato da @phomea in kb#145 l'08/09).
     *
     * ⚠️ **Non prende un id**: agisce SOLO sull'attore autenticato. E' il motivo per cui
     * questa rotta e quella del monolite (`PUT /users/:id`) non sono intercambiabili con
     * un'alias — hanno forme diverse perche' hanno modelli di autorizzazione diversi. Chi
     * le sceglie e' `hooks/useSalvaNickname.ts`, che e' l'unico posto in cui la scelta vive.
     *
     * ⚠️ Il server normalizza trim/lowercase e pretende `[a-z0-9_]` 3-32: il rifiuto di
     * formato e' **400**, non 422, e il nome occupato e' **409**. I due sono esiti diversi
     * per l'utente (`lib/nicknameEsito.ts`) — collassarli manderebbe a cambiare un nome che
     * andava bene. `404` = account assente/inattivo, `503` = indice di unicita' non
     * disponibile: ⛔ **quest'ultimo NON e' «gia' preso»** — il server dichiara di non poter
     * garantire l'unicita', cioe' di non sapere, e finisce in `sconosciuto` per costruzione
     * (`esitoScritturaNickname` mappa solo 409/400/422).
     */
    putLimuloNickname: builder.mutation<unknown, { nickname: string }>({
      query: (body) => ({ url: '/v1/me/nickname', method: 'PUT', body }),
      invalidatesTags: ['LimuloMe'],
    }),

    // ── Fase 3: le quindici rotte del collasso «sezione Laravel → facciata» ──────
    // Forme dal mirror SERVITO `qlash-limulo@91743eb`. Nessuna schermata le chiama
    // ancora: il cablaggio della UI e' il giro successivo.

    /**
     * `POST /v1/tournaments/:id/registration` (`router.js:1824-1846`, 201).
     *
     * ⚠️ NON si manda `spec.partySize`: la forma della parte la dichiara il TORNEO, e un
     * valore diverso e' un rifiuto nominato (`iscrizioni.js:323-332`). Prima del
     * 2026-08-24 il payload vinceva sul torneo, ed era il varco da cui una parte di
     * gruppo entrava dalla porta dell'individuale.
     *
     * 🔴 `acceptedRulesVersion` (numero) e' il CONSENSO al regolamento del torneo (qTournaments 1.93.0): il BFF
     * lo inoltra al provider (il corpo passa intero meno i campi vietati, `policy.js:11`
     * [T limulo-core-orcr 14:26Z, staging]) e chi lo compone e' `registrationBody`. Un torneo senza regolamento
     * non lo manda: il corpo resta quello di prima.
     *
     * ⚠️ E non esiste `secret_code`: sulla facciata **non c'e' il torneo privato**. Chi
     * porta qui la schermata Laravel deve togliere il foglio password, non tradurlo.
     */
    postLimuloRegistration: builder.mutation<
      LimuloRegistrationResult | null,
      { id: string; name?: string; members?: string[]; profileId?: string; acceptedRulesVersion?: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/registration`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloRegistrationResult>(response),
      // L'iscrizione cambia `participantsCount`, che la lista mostra: invalidare solo lo
      // stato d'iscrizione lascerebbe a schermo un conteggio vecchio di un posto.
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LimuloRegistration' as const, id },
        { type: 'LimuloTournament' as const, id },
        'LimuloTournament' as const,
      ],
    }),

    /**
     * `DELETE /v1/tournaments/:id/registration` — **il ritiro** (`router.js:2104-2127`,
     * catalogo `qlash.tournaments.registration.withdraw`, misurato su
     * `qlash-limulo@origin/staging` = `74052bb` il 2026-09-08, mirror qTournaments 1.18.2).
     *
     * ⚠️ **NESSUN CORPO, e non e' una svista.** Il BFF costruisce il payload da una sorgente
     * VUOTA — non `req.body`, non `req.query` (`router.js:2109-2116`): la parte la deriva
     * dall'attore autenticato. Un `partyId` o un `profileId` mandati da qui sarebbero
     * un'identita' scelta da chi chiama, ed e' esattamente la cosa che quella riga impedisce.
     *
     * 🔴 **La risposta ha la forma della GET, non della POST.** `sendOk(res, req,
     * sanitizeRegistrationStatus(data))` (`router.js:2123`) — quindi porta `registered`,
     * che la POST non porta. Resta comunque `withdrawReconciled` a rileggere: il rifiuto
     * nominato arriva muto come tutti gli altri (424), e un ritiro che il server ha respinto
     * si distingue da uno riuscito solo guardando cosa il server HA.
     */
    deleteLimuloRegistration: builder.mutation<LimuloRegistrationStatus | null, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/registration`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloRegistrationStatus>(response),
      // Stessa ragione dell'iscrizione: il ritiro cambia `participantsCount`, e invalidare
      // solo lo stato lascerebbe a schermo un conteggio vecchio di un posto.
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LimuloRegistration' as const, id },
        { type: 'LimuloTournament' as const, id },
        'LimuloTournament' as const,
      ],
    }),

    /**
     * `POST /v1/tournaments/:id/registration/checkin` — **il check-in AL TORNEO**
     * (`router.js:2223-2249` @ `qlash-limulo#108`, testa `c4ac436`, **ancora OPEN**: sul servito
     * di oggi la rotta non c'e', e la barra non la chiama finche' il bordo non porta `checkIn`).
     *
     * ⚠️ **NESSUN CORPO E NESSUNA QUERY, di proposito**: il BFF costruisce il payload da una
     * sorgente VUOTA (`source: {}`), come per il ritiro — la parte la deriva dall'attore. Mandare
     * da qui un'identita' sarebbe scegliere per conto di chi chiama.
     * ⛔ Non e' `postLimuloMatchCheckin` (il check-in dell'INCONTRO, `…/matches/:matchId/checkin`,
     * che manda `body: {}` ed e' un'altra cosa).
     *
     * La risposta ha la forma della GET (`sanitizeRegistrationStatus`), ed e' idempotente lato
     * provider. Si rilegge lo stesso: v. `checkinReconciled`.
     */
    postLimuloTournamentCheckin: builder.mutation<LimuloRegistrationStatus | null, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/registration/checkin`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloRegistrationStatus>(response),
      // Una presenza non cambia `participantsCount`: si invalida lo stato d'iscrizione e basta.
      invalidatesTags: (_r, _e, { id }) => [{ type: 'LimuloRegistration' as const, id }],
    }),

    /**
     * `GET /v1/tournaments/:id/matches` (`router.js:1855-1880`).
     *
     * ⚠️ IL FILTRO PER PARTECIPANTE NON SI MANDA. `stripMatchClientFields`
     * (`router.js:307-320`) toglie `participantId` dal payload prima che arrivi al
     * provider: la richiesta tornerebbe con TUTTI gli incontri e 200, indistinguibile da
     * un filtro che non ha escluso nessuno. Si chiede la lista e si filtra qui.
     * L'unico filtro che sopravvive e' il round (`incontri.js:289-301`).
     */
    getLimuloMatches: builder.query<
      LimuloMatch[],
      { id: string; round?: number; participantId?: string }
    >({
      query: ({ id, round }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches`,
        params: round === undefined ? undefined : { round },
      }),
      transformResponse: (response: unknown, _meta, { participantId }) =>
        matchesOfParticipant(unwrapItems<unknown>(response).map(matchOf), participantId),
      providesTags: (result) => [
        'LimuloMatch' as const,
        ...(result ?? []).map((m) => ({ type: 'LimuloMatch' as const, id: m.matchId })),
      ],
    }),

    /**
     * **La Live della Hub CR, lettura UNICA** (I-224, lotto 2 di L-2315 a): gli incontri in corso di
     * UN gioco, `GET /v1/tournaments/live-matches` (qlash-limulo#169 → `tournament.match.live.list`,
     * qTournaments 1.79.0). Il motore filtra gia' «in corso» (diviso per modo, kb#299), e ogni riga
     * porta il suo torneo (`origine`, `queueName`) e i link spettatore proiettati stretti.
     * `limit` e' il tetto del BFF (100, `toPositiveInt(source.limit, 50, 100)`); oltre, la scheda dice
     * quante non mostra (`total`). Non chiede nessuna iscrizione: basta un utente autenticato.
     * ⚠️ Il lotto 1 leggeva a VENTAGLIO (`/v1/tournaments?status=in_progress` e poi il tabellone di
     * ogni torneo con `scope=all`): 1 + T chiamate ogni 15 s per telefono. Ora e' una per gioco CR.
     */
    getLimuloPartiteInCorso: builder.query<LimuloPartiteLive, { gameId: string }>({
      query: ({ gameId }) => ({
        url: '/v1/tournaments/live-matches',
        params: { gameId, page: 1, limit: 100 },
      }),
      transformResponse: (response: unknown) => partiteLiveOf(unwrapData<unknown>(response)),
      providesTags: ['LimuloMatch'],
    }),

    /** `GET /v1/tournaments/:id/matches/:matchId` (`router.js:2066-2085`). */
    getLimuloMatch: builder.query<LimuloMatch | null, { id: string; matchId: string }>({
      query: ({ id, matchId }) =>
        `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}`,
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchOf(data);
      },
      providesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /**
     * L'ID di gioco dell'altro lato (ADR-0169 §7, lotto T6): `tournament.match.game-ids.read` di
     * qTournaments, che il BFF espone su una rotta di DETTAGLIO sua.
     * Percorso proposto da noi (kb#292 3b-i) e confermato da @phomea: qlash-limulo#159 (`3441fa5`), busta del
     * motore inoltrata senza modifiche; la capability e' su `origin/staging` dal 2026-09-28T17:58Z (qT 1.85.1).
     * ⚠️ Un rifiuto (non parte, incontro concluso) arriva come errore e il riquadro si NASCONDE:
     * chi non ha titolo non vede niente, nemmeno un «non puoi». Nessun polling: durante un incontro
     * gli ID non cambiano.
     */
    getLimuloMatchGameIds: builder.query<LimuloMatchGameIds | null, { id: string; matchId: string }>({
      query: ({ id, matchId }) =>
        `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/game-ids`,
      transformResponse: (response: unknown) => gameIdsOf(unwrapData<unknown>(response)),
    }),

    /** `GET /v1/tournaments/:id/matches/:matchId/result` (`router.js:1882-1901`). */
    getLimuloMatchResult: builder.query<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) =>
        `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/result`,
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      providesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /**
     * `POST …/matches/:matchId/result` (`router.js:1903-1922`).
     *
     * ⚠️ Il punteggio e' OBBLIGATORIO e ha una forma sua: `{home, away}` numerici
     * (`incontri.js:626-633`), non la stringa `"3:1"` che il monolite accetta. Il primo
     * invio vince; l'identico e' idempotente; il diverso prende un rifiuto nominato.
     *
     * ⚠️ Nel corpo non entra nient'altro. Il server non spoglia `providerResult` dal body
     * (finding aperto, riportato da qgammon-orc): il client non lo nomina mai.
     */
    postLimuloMatchResult: builder.mutation<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string; score: { home: number; away: number }; externalRef?: string }
    >({
      query: ({ id, matchId, score, externalRef }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/result`,
        method: 'POST',
        body: { score, ...(externalRef === undefined ? {} : { externalRef }) },
      }),
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      // Un referto muove la classifica del torneo, non solo la riga dell'incontro.
      invalidatesTags: (_r, _e, { id, matchId }) => [
        { type: 'LimuloMatch' as const, id: matchId },
        'LimuloMatch' as const,
        { type: 'LimuloTournament' as const, id },
      ],
    }),

    /**
     * `POST …/matches/:matchId/result-media` (`router.js:1924-1983`) — multipart.
     *
     * ⚠️ E' il SECONDO passo, non un'alternativa al referto: il server registra l'asset
     * (`media.asset.register`, app `media`) e poi lo allega. Sul monolite l'immagine si
     * postava sullo STESSO `/result` del punteggio (`endpoints/matches.ts:40-53`): qui
     * prima si manda `score`, poi la prova.
     *
     * ⚠️ Nessun `Content-Type`: lo scrive il browser col boundary, e forzarlo rompe
     * l'upload. Il campo si chiama `file` (`router.js:762-764`), le immagini ammesse sono
     * jpeg/png/webp/gif fino a 15 MB (`:59-66`).
     *
     * ⚠️ Se l'app `media` non e' attiva sull'organizzazione, questa rotta risponde **424**
     * (nessun provider per la capability), non 403. Lo stato dell'app NON si legge da qui:
     * e' configurazione e cambia senza preavviso — la fonte viva, col grado di ogni riga,
     * e' `services/qlash-limulo/identita.md` nel KB. Qui c'era «su staging e' spenta»,
     * cioe' un fatto sul mondo scritto al presente: la stessa forma che su `qlash-api` e'
     * rimasta falsa per tre giorni senza che niente lo segnalasse.
     */
    postLimuloMatchResultMedia: builder.mutation<
      LimuloResultMediaAttach | null,
      { id: string; matchId: string; file: File; clientMutationId?: string }
    >({
      query: ({ id, matchId, file, clientMutationId }) => {
        const form = new FormData();
        form.append('file', file);
        if (clientMutationId !== undefined) form.append('clientMutationId', clientMutationId);
        return {
          url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/result-media`,
          method: 'POST',
          body: form,
        };
      },
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : resultMediaAttachOf(data);
      },
      invalidatesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /**
     * `POST …/matches/:matchId/checkin` (`router.js:1985-2004`), corpo vuoto.
     * Ripetuto e' `gia-registrato`, non un errore (`catalogo.js:193`).
     *
     * ⚠️ Questo e' il check-in dell'INCONTRO. Il check-in di TORNEO del monolite
     * (`PUT /tournaments/:id/participants`) non esiste sulla facciata ed e' stato
     * rinunciato come prodotto (L-88, decisione dell'operatore del 2026-09-02).
     */
    postLimuloMatchCheckin: builder.mutation<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/checkin`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      // Una presenza non tocca la classifica: si invalida l'incontro e basta.
      invalidatesTags: (_r, _e, { matchId }) => [{ type: 'LimuloMatch' as const, id: matchId }],
    }),

    /**
     * «NON SONO PRONTO» — `POST …/matches/:matchId/checkin` con `{ ready: false }`
     * (qTournaments 1.69.0, ADR-0158; il bordo con `qlash-limulo#134`, testa `a467d11`).
     *
     * 🔴 **E' UN FORFAIT IMMEDIATO E DEFINITIVO**: l'incontro e' perso a tavolino, l'altro lato
     * vince anche se assente, e il motore non chiede conferma — «il "sei sicuro?" spetta alla UI»
     * (kb#268). Per questo e' una mutation SUA e non un parametro di `postLimuloMatchCheckin`: la
     * stessa rotta, ma un corpo che nessun altro gesto puo' produrre per sbaglio.
     * ⛔ Il «sono pronto» resta quello di sopra, col corpo vuoto di sempre. `ready: true` non si
     * manda mai: e' identico al corpo vuoto, e una seconda forma dello stesso gesto e' un posto in
     * piu' dove un `false` puo' finire. `tools/verify-limulo.ts` §24 lo tiene.
     * ⚠️ La rotta esiste sul bordo di oggi e INOLTRA il corpo (`source: req.body`): e' la UI a
     * esporre il gesto solo quando `matchCheckIn` dice che la finestra e' aperta.
     */
    postLimuloMatchNotReady: builder.mutation<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string }
    >({
      query: ({ id, matchId }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/checkin`,
        method: 'POST',
        body: { ready: false },
      }),
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      // Un forfait e' un referto marcato: stessa invalidazione del ritiro (la classifica cambia).
      invalidatesTags: (_r, _e, { id, matchId }) => [
        { type: 'LimuloMatch' as const, id: matchId },
        'LimuloMatch' as const,
        { type: 'LimuloTournament' as const, id },
      ],
    }),

    /**
     * `POST …/matches/:matchId/forfeit` (`router.js:2006-2025`).
     *
     * ⚠️ `reason` e' OBBLIGATORIO (`incontri.js:1112-1117`, ADR-0019): «un forfeit senza
     * motivo e' una decisione arbitrale che nessuno potra' piu' spiegare». Il corpo vuoto
     * che manda il monolite (`endpoints/matches.ts:77-87`) qui e' un 4xx nominato — la UI
     * dovra' chiedere il motivo, non solo confermare.
     */
    postLimuloMatchForfeit: builder.mutation<
      LimuloMatchResultPayload | null,
      { id: string; matchId: string; reason: string }
    >({
      query: ({ id, matchId, reason }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/forfeit`,
        method: 'POST',
        body: { reason },
      }),
      transformResponse: (response: unknown) => {
        const data = unwrapData<unknown>(response);
        return data === null ? null : matchResultPayloadOf(data);
      },
      // Un forfeit e' un referto marcato: stessa invalidazione del referto.
      invalidatesTags: (_r, _e, { id, matchId }) => [
        { type: 'LimuloMatch' as const, id: matchId },
        'LimuloMatch' as const,
        { type: 'LimuloTournament' as const, id },
      ],
    }),

    // ── La chat dell'incontro (eFootball T3, 2026-09-26) ──────────────────────
    // `router.js:2484-2513` @ `qlash-limulo@origin/staging` = `7e4d86e`: tre `handleCapabilityJson`
    // con `assertTournamentMatchAccess` e SENZA sanitizer ⇒ la forma grezza del motore
    // (`app-qtournaments/app/core/chat.js`) e' il contratto. Si legge in `lib/limulo/chatIncontro.ts`.
    // ⚠️ MAI misurate dal vivo da noi: la prova e' il collaudo. Chi le usa: `LimuloMatchChat`.

    /**
     * `GET …/matches/:matchId/chat` — la STANZA (`tournament.match.chat.resolve`): i partecipanti
     * col nome PUBBLICO (il bordo la passa da `withPublicParticipantNames`, e' una capability
     * `tournament.*`). Da qui, e solo da qui, vengono i nomi degli autori.
     * ⚠️ Nessun `pollingInterval`: chi c'e' nella stanza non cambia durante un incontro; la
     * rileggono il rientro sull'app e la riconnessione, come tutto `limuloApi`.
     */
    getLimuloMatchChat: builder.query<LimuloMatchChatRoom | null, { id: string; matchId: string }>({
      query: ({ id, matchId }) =>
        `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/chat`,
      transformResponse: (response: unknown) => stanzaChatOf(unwrapData<unknown>(response)),
      providesTags: (_r, _e, { matchId }) => [{ type: 'LimuloChat' as const, id: `stanza:${matchId}` }],
    }),

    /**
     * `GET …/matches/:matchId/messages?page=1&limit=50` — la PRIMA pagina, cioe' i messaggi piu'
     * recenti, in ordine ascendente dentro la pagina (`listaMessaggi`). `total` conta anche
     * eliminati e moderati. Niente paginazione all'indietro per ora: la schermata dice quanti ne
     * restano fuori (`messaggiPrecedenti`).
     * ⚠️ La busta: `data = {room, items, page, limit, total}` e la `pagination` FUORI da `data`
     * (`sendCapabilityDataOk`, `router.js:603-611`). `unwrapItems` da solo perderebbe `total`.
     */
    getLimuloMatchMessages: builder.query<LimuloChatPage, { id: string; matchId: string }>({
      query: ({ id, matchId }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/messages`,
        params: { page: 1, limit: LIMULO_CHAT_PAGINA },
      }),
      transformResponse: (response: unknown) =>
        paginaChatOf(unwrapData<unknown>(response), unwrapPagination(response)),
      providesTags: (_r, _e, { matchId }) => [{ type: 'LimuloChat' as const, id: matchId }],
    }),

    /**
     * `POST …/matches/:matchId/messages` (201) — l'invio (`tournament.match.message.send`).
     *
     * ⚠️ **Nel corpo va `body` e NIENT'ALTRO.** Il motore accetta anche `message`/`text` (tre nomi
     * per la stessa cosa) e `attachments`/`metadata`: una sola forma e' un posto solo dove guardare.
     * L'autore NON si manda — il motore lo ricava dall'attore (`attoreDi`), e un `senderId` dal
     * client sarebbe un'identita' scelta da chi chiama. Il `trim` e il tetto li fa `bozzaChat`
     * prima di arrivare qui.
     * ⚠️ La risposta e' `{room, message}`, non il messaggio: v. `messaggioInviatoOf`.
     * ⛔ Mai ritentata: e' una POST (`retryCondition`), e un messaggio ripetuto da solo sarebbe un
     * doppione in chat.
     */
    postLimuloMatchMessage: builder.mutation<
      LimuloChatMessage | null,
      { id: string; matchId: string; body: string }
    >({
      query: ({ id, matchId, body }) => ({
        url: `/v1/tournaments/${encodeURIComponent(id)}/matches/${encodeURIComponent(matchId)}/messages`,
        method: 'POST',
        body: { body },
      }),
      transformResponse: (response: unknown) => messaggioInviatoOf(unwrapData<unknown>(response)),
      // Un messaggio cambia la lista di QUELL'incontro e nient'altro: non la stanza (chi c'e' non
      // cambia), non l'incontro, non la classifica.
      invalidatesTags: (_r, _e, { matchId }) => [{ type: 'LimuloChat' as const, id: matchId }],
    }),

    /**
     * `GET /v1/games` (`router.js:1644-1660`), provider `app-games@220`.
     *
     * Serve a chi deve ELENCARE i giochi, e a chi deve risolvere il `gameId` di un torneo:
     * il `profileType` (lobby backgammon, scheda dell'incontro, azione persistente) e, per la
     * sola etichetta, il nome (`useLimuloGameName`, card dei tornei). ⚠️ Il vincolo kb#81 del
     * «passthrough» e' superato per l'etichetta, non per il codice: se il catalogo non
     * risponde si mostra il codice che il torneo porta (`gameNameOf`).
     * ⚠️ E `id` qui e' un ObjectId, non l'id numerico del monolite (`CR_GAME_ID = 6`):
     * il selettore di gioco della TMA non riconoscerebbe nessuna di queste righe.
     */
    getLimuloGames: builder.query<LimuloGame[], void>({
      query: () => '/v1/games',
      transformResponse: (response: unknown) => unwrapItems<LimuloGame>(response),
      providesTags: ['LimuloGame'],
    }),

    /**
     * `GET /v1/games/:gameId/match-modes` (limulo#152 `bd61d4d`, `router.js:1983`; kb#297 domanda 2): le
     * modalita' ATTIVE di un gioco, cioe' `{gameId, items}` dentro `sendOk`. La legge il pannello della
     * sfida, per il solo gioco scelto.
     * ⚠️ [R @phomea, kb#297 27/09 06:49Z] Un'utenza senza identita' mobile riceve 500 nel percorso del
     * provider: e' un limite gia' visto sul catalogo, non un guasto della rotta.
     */
    getLimuloGameMatchModes: builder.query<LimuloGameMatchMode[], string>({
      query: (gameId) => `/v1/games/${encodeURIComponent(gameId)}/match-modes`,
      transformResponse: (response: unknown) => unwrapItems<LimuloGameMatchMode>(response),
      providesTags: ['LimuloGame'],
    }),

    // ── La sfida fra amici (I-207, T2, contratto PR169 fusa 2026-09-28 `f0db426`) ──────────

    /**
     * `POST /v1/challenges` — 201. **Sempre NOMINATIVA** (`target: {sub}`): niente `by-code`
     * (dispatch sfide-orcr-3-invio, vincolo). Il corpo lo compone `createChallengeBody`.
     * ⚠️ Il rifiuto arriva con `details.challengeReason` (`challengeReasonOf`, `errors.ts`), MAI
     * col vocabolario dell'iscrizione ai tornei — sono due famiglie di motivi distinte.
     */
    createLimuloChallenge: builder.mutation<
      LimuloChallengeWithIdentities | null,
      { gameId: string; gameModeKey: string; targetSub: string; profileId?: string | null }
    >({
      query: ({ gameId, gameModeKey, targetSub, profileId }) => ({
        url: CHALLENGE_ROUTES.createOrList,
        method: 'POST',
        body: createChallengeBody({ gameId, gameModeKey, targetSub, profileId }),
      }),
      transformResponse: (response: unknown) => challengeOf(unwrapData<unknown>(response)),
      invalidatesTags: ['LimuloChallenge'],
    }),

    /**
     * `GET /v1/challenges?direction=in|out&state=…&page=…&limit=…`. `page`/`limit` solo cifre
     * (400 altrimenti, contratto PR169): li manda solo chi li passa, mai un default a caso.
     * ⚠️ Non e' consumata da questo lotto (invio): esiste perche' il TASK la chiede al punto 1, e
     * la usera' il lotto B (ricezione/liste) — vive qui perche' il client e' uno per servizio.
     */
    getLimuloChallenges: builder.query<
      LimuloChallengeList,
      { direction: 'in' | 'out'; state?: string; page?: number; limit?: number }
    >({
      query: ({ direction, state, page, limit }) => ({
        url: CHALLENGE_ROUTES.createOrList,
        params: {
          direction,
          ...(state === undefined ? {} : { state }),
          ...(page === undefined ? {} : { page }),
          ...(limit === undefined ? {} : { limit }),
        },
      }),
      transformResponse: (response: unknown) => challengeListOf(response),
      providesTags: (result) => [
        'LimuloChallenge' as const,
        ...(result?.items ?? []).map((c) => ({ type: 'LimuloChallenge' as const, id: c.challengeId })),
      ],
    }),

    /** `GET /v1/challenges/:challengeId`. */
    getLimuloChallenge: builder.query<LimuloChallengeWithIdentities | null, string>({
      query: (challengeId) => CHALLENGE_ROUTES.read(challengeId),
      transformResponse: (response: unknown) => challengeOf(unwrapData<unknown>(response)),
      providesTags: (_r, _e, challengeId) => [{ type: 'LimuloChallenge' as const, id: challengeId }],
    }),

    /** `POST /v1/challenges/:challengeId/accept` (body `profileId?`). */
    acceptLimuloChallenge: builder.mutation<
      LimuloChallengeWithIdentities | null,
      { challengeId: string; profileId?: string | null }
    >({
      query: ({ challengeId, profileId }) => {
        const profilo = (profileId ?? '').trim();
        return {
          url: CHALLENGE_ROUTES.accept(challengeId),
          method: 'POST',
          body: profilo === '' ? {} : { profileId: profilo },
        };
      },
      transformResponse: (response: unknown) => challengeOf(unwrapData<unknown>(response)),
      invalidatesTags: (_r, _e, { challengeId }) => [
        { type: 'LimuloChallenge' as const, id: challengeId },
        'LimuloChallenge' as const,
        'LimuloTournament' as const,
      ],
    }),

    /** `POST /v1/challenges/:challengeId/decline` — nessun corpo. */
    declineLimuloChallenge: builder.mutation<LimuloChallengeWithIdentities | null, { challengeId: string }>({
      query: ({ challengeId }) => ({ url: CHALLENGE_ROUTES.decline(challengeId), method: 'POST' }),
      transformResponse: (response: unknown) => challengeOf(unwrapData<unknown>(response)),
      invalidatesTags: (_r, _e, { challengeId }) => [
        { type: 'LimuloChallenge' as const, id: challengeId },
        'LimuloChallenge' as const,
      ],
    }),

    /** `POST /v1/challenges/:challengeId/revoke` — nessun corpo: solo chi ha sfidato puo' ritirarla. */
    revokeLimuloChallenge: builder.mutation<LimuloChallengeWithIdentities | null, { challengeId: string }>({
      query: ({ challengeId }) => ({ url: CHALLENGE_ROUTES.revoke(challengeId), method: 'POST' }),
      transformResponse: (response: unknown) => challengeOf(unwrapData<unknown>(response)),
      invalidatesTags: (_r, _e, { challengeId }) => [
        { type: 'LimuloChallenge' as const, id: challengeId },
        'LimuloChallenge' as const,
      ],
    }),

    /** `GET /v1/platforms` (`router.js:1626-1642`). */
    getLimuloPlatforms: builder.query<LimuloPlatform[], void>({
      query: () => '/v1/platforms',
      transformResponse: (response: unknown) => unwrapItems<LimuloPlatform>(response),
      providesTags: ['LimuloGame'],
    }),

    /**
     * `GET /v1/me/favorite-games` (`router.js:1689`).
     *
     * ⚠️ RISPONDE UN OGGETTO, non una lista: `sanitizeFavoriteGameResult`
     * (`policy.js:178-187`) dentro `sendOk` (`router.js:1683-1685`). `unwrapItems` qui
     * darebbe `[]` con 200 — «nessun preferito» dove la verita' e' un'altra forma.
     * L'alias `/v1/users/:userId/favourite-games` (`:1690`) non si usa: accetta solo il
     * proprio id (`:1671-1674`), quindi non aggiunge niente e ne chiede uno in piu'.
     */
    getLimuloFavoriteGames: builder.query<LimuloFavoriteGames, void>({
      query: () => '/v1/me/favorite-games',
      transformResponse: (response: unknown) => favoriteGamesOf(unwrapData(response)),
      providesTags: ['LimuloGame'],
    }),

    /**
     * `GET /v1/games/:gameId/profile-requirements` (`router.js:2218-2235`) — che cosa serve
     * per creare il profilo di gioco di UN gioco.
     *
     * 🔴 **E' l'unica rotta di questa sezione SENZA facciata**: l'handler fa
     * `sendOk(res, req, data)` sul dato grezzo del provider, senza `sanitize*`. La forma e'
     * quindi al 100% di `readGameProfileRequirements`
     * (`app-games/runtime-services.js:1781-1830`) e puo' cambiare senza che il BFF cambi —
     * stessa classe di `standings`. Per questo il tipo qui e' `unknown` e la lettura la fa
     * `profileFormPlan` (`lib/limulo/registration.ts`) campo per campo: dichiarare
     * un'interfaccia darebbe a un contratto non promesso la faccia di uno promesso.
     *
     * ⚠️ Il `:gameId` e' il CODICE del gioco (`tournament.gameId`, passthrough kb#81), come
     * per ogni altra giunzione di questa sezione — mai un ObjectId.
     */
    getLimuloGameProfileRequirements: builder.query<unknown, string>({
      query: (gameId) => `/v1/games/${encodeURIComponent(gameId)}/profile-requirements`,
      transformResponse: (response: unknown) => unwrapData<unknown>(response),
      // Dipende dai profili dell'utente (`status` ci finisce dentro), non solo dal gioco:
      // creato un profilo, i requisiti letti prima non descrivono piu' la stessa situazione.
      providesTags: ['LimuloProfile', 'LimuloGame'],
    }),

    /**
     * `GET /v1/games/profiles` (`router.js:2105-2124`) — i profili del CHIAMANTE.
     * Il `:id` del path Laravel sparisce: l'utente lo mette il server dal JWT (`:2113`).
     */
    getLimuloProfiles: builder.query<LimuloProfile[], { activeOnly?: boolean } | void>({
      query: (args) => ({
        url: '/v1/games/profiles',
        params: args && args.activeOnly !== undefined ? { activeOnly: args.activeOnly } : undefined,
      }),
      transformResponse: (response: unknown) => unwrapItems<LimuloProfile>(response),
      providesTags: ['LimuloProfile'],
    }),

    /**
     * `GET /v1/games/profiles/:profileId` (`router.js:2126-2140`).
     * Un profilo di un altro utente risponde 403 (`assertProfileBelongsToUser`, `:302-309`).
     */
    getLimuloProfile: builder.query<LimuloProfile | null, string>({
      query: (profileId) => `/v1/games/profiles/${encodeURIComponent(profileId)}`,
      transformResponse: (response: unknown) => unwrapData<LimuloProfile>(response),
      providesTags: (_r, _e, profileId) => [{ type: 'LimuloProfile' as const, id: profileId }],
    }),

    /**
     * `POST /v1/games/profiles` (`router.js:2142-2156`, 201).
     *
     * ⚠️ I NOMI SONO QUELLI DEL PROVIDER, non del monolite: `gameId` (codice, non il 6
     * numerico), `platformId`, `externalPlayerId` — dove Laravel manda
     * `{game_id, platform_id, info: {id, nickname}}` (`endpoints/games.ts:43-50`).
     * Letti su `app-games/runtime-services.js:556+` (`normalizePlayerPayload`).
     *
     * ⚠️ E l'upsert bulk del monolite qui NON esiste: sono due rotte, e «crea o aggiorna»
     * diventa «leggi la lista, poi POST oppure PATCH».
     */
    createLimuloProfile: builder.mutation<
      LimuloProfile | null,
      /**
       * ⚠️ L'argomento e' APERTO di proposito, e prima era un letterale a cinque chiavi che
       * costringeva il chiamante a un `as never` — cioe' a spegnere il compilatore su un
       * contratto falso.
       *
       * I campi veri li dichiara il SERVER, gioco per gioco
       * (`GET /v1/games/:gameId/profile-requirements`): per Clash Royale sono `playerTag` e
       * `apiToken`, che qui non c'erano. E non sono nemmeno colonne del profilo —
       * `normalizeProviderPayload` (`app-games/runtime-services.js:249-271`) instrada le
       * chiavi di `PROVIDER_PAYLOAD_FIELDS` dentro `meta.provider`. Elencarle qui
       * significherebbe ricopiare uno schema che cambia con i giochi, in un posto che nessun
       * test lega alla fonte.
       * ⇒ `gameId` e' l'unica chiave che il BFF pretende sempre; il resto lo compone
       * `profileFormBody` (`lib/limulo/registration.ts`) dai campi dichiarati dal server.
       * ⛔ `userId` non entra mai: lo mette il server (`buildSafeProfilePayload`).
       */
      { gameId: string } & Record<string, string>
    >({
      query: (body) => ({ url: '/v1/games/profiles', method: 'POST', body }),
      transformResponse: (response: unknown) => unwrapData<LimuloProfile>(response),
      invalidatesTags: ['LimuloProfile'],
    }),

    /**
     * `PATCH /v1/games/profiles/:profileId` (`router.js:2158-2181`).
     *
     * ⚠️ **L'argomento e' APERTO come quello del POST, e per la stessa ragione**: i campi veri
     * li dichiara il server gioco per gioco, e `normalizeProviderPayload` instrada le chiavi
     * di `PROVIDER_PAYLOAD_FIELDS` dentro `meta.provider`. Prima qui c'era un letterale a
     * quattro chiavi **senza i campi del provider**, e quel letterale non era una svista: era
     * il contratto che impediva di ricollegare un profilo, perche' un tag nuovo non poteva
     * nemmeno essere scritto nel corpo.
     *
     * 🔑 **E il PATCH RIVALIDA**: `updatePlayerProfile` (`app-games/runtime-services.js:2056`)
     * chiama `validatePlayerProfilePayload` (`:2082`), lo stesso della creazione. Misurato su
     * `stg-tma` il 2026-09-12 (I-20), tre chiamate:
     *   · `PATCH {playerTag}`                  -> `200`, ma il tag NON cambia (rivalida e basta)
     *   · `PATCH {externalPlayerId}` da solo   -> `400` (senza token la validazione rifiuta)
     *   · `PATCH {externalPlayerId, apiToken}` -> `200`, tag cambiato e `validatedAt` nuovo
     * ⇒ **ricollegare = tag nuovo + token fresco**, cioe' gli stessi campi della creazione.
     */
    updateLimuloProfile: builder.mutation<
      LimuloProfile | null,
      { profileId: string } & Record<string, string>
    >({
      query: ({ profileId, ...body }) => ({
        url: `/v1/games/profiles/${encodeURIComponent(profileId)}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloProfile>(response),
      invalidatesTags: (_r, _e, { profileId }) => [
        { type: 'LimuloProfile' as const, id: profileId },
        'LimuloProfile' as const,
      ],
    }),

    /**
     * `POST /v1/games/profiles/:profileId/sync` (`app-qlash-api/backend/router.js:2591` @
     * qlash-limulo origin/staging), SENZA corpo.
     *
     * 🔴 IL REPERTO (qgammon-orc 2026-09-24 23:29Z, GO di Luca «A»): su un torneo backgammon la
     * partita esterna nasce in `conflict` per `sub_missing` su ENTRAMBI i giocatori. I loro profili
     * li ha creati la TMA col profilo implicito (#377), e il `sync` del provider — l'unico che
     * scrive `meta.provider.sub` ricavandolo dall'attore — non era mai partito: la creazione da
     * sola non lo scrive. ⇒ Chi crea il profilo (o apre una scheda con un profilo a zero campi)
     * chiede anche il sync: regola e sedi in `lib/limulo/registration.ts` (`sincronizzaProfilo`).
     *
     * Il BFF controlla l'appartenenza, manda al provider `{idOrCode, userId}` (l'utente lo mette
     * lui dal JWT: dal client non si manda niente) e rilegge il profilo: la risposta e'
     * `{sync, profile}`. Il tipo resta `unknown` perche' nessun chiamante la legge — conta
     * l'esito, e il profilo aggiornato arriva dalla rilettura che l'invalidazione provoca.
     */
    postLimuloProfileSync: builder.mutation<unknown, string>({
      query: (profileId) => ({
        url: `/v1/games/profiles/${encodeURIComponent(profileId)}/sync`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => unwrapData<unknown>(response),
      // Il sync riscrive `meta.provider` del profilo: la lista e il singolo vanno riletti.
      invalidatesTags: (_r, _e, profileId) => [
        { type: 'LimuloProfile' as const, id: profileId },
        'LimuloProfile' as const,
      ],
    }),

    /**
     * `POST /v1/auth/logout` (`router.js:711-723`).
     *
     * ⚠️ Il `refreshToken` va nel CORPO (`mobile-auth.js:905`): senza, il server prova a
     * risalire alla sessione dal Bearer, e se il token e' gia' scaduto non revoca niente.
     *
     * ⚠️ NESSUN TAG: questa mutation non invalida cache, la BUTTA — ma svuotare lo store
     * e' compito di chi gestisce la sessione, non di una query.
     *
     * ✅ CABLATO dal 07/10 (tma-orcr-35), ma NON da qui: «Esci» chiama
     * `revocaEChiudiLimuloSession()` (`lib/limulo/session.ts`), che fa la POST con `fetch` e
     * non passa da questa mutation. Ragione: il suo `prepareHeaders` chiama `getLimuloToken()`,
     * che a sessione scaduta RINNOVA — ruotando il refresh token proprio mentre lo si butta —
     * e l'`Authorization` non serve (il refresh token nel corpo basta). Resta senza
     * consumatori di proposito; ⚠️ chi la usera' per un'altra uscita (cambio account, grant
     * morto: `handleDeadGrant` NON revoca ancora niente) erediti questo avvertimento.
     * ~~Oggi `endLimuloSession()` cancella solo il `localStorage` e la sessione resta VIVA sul
     * server fino a scadenza~~ — vero fino al 07/10, per ogni «Esci».
     */
    postLimuloLogout: builder.mutation<LimuloLogoutResult | null, { refreshToken: string }>({
      query: ({ refreshToken }) => ({
        url: '/v1/auth/logout',
        method: 'POST',
        body: { refreshToken },
      }),
      transformResponse: (response: unknown) => unwrapData<LimuloLogoutResult>(response),
    }),

    // ── Le code (qTournaments 1.72.0, ADR-0163, contratto kb#282) ─────────────
    // Dichiarate da qlash-limulo#146, servite da qlash-limulo#151. I path stanno in `QUEUE_ROUTES`
    // (`lib/limulo/code.ts`), che e' l'unico posto dove si scrivono. Le monta la «Ranked» della Hub CR
    // (e il biglietto `CodeTorneiContext`) solo con `VITE_FEATURE_TOURNAMENT_QUEUES_ENABLED`, acceso
    // solo in `.env.staging`.

    /**
     * `tournament.queue.list` — i modelli ATTIVI (coda 1v1 e sit&go) con quanti aspettano, e i
     * biglietti di chi chiama. `gameId` restringe a un gioco. Nessun parametro identita'.
     */
    getLimuloQueues: builder.query<LimuloQueueList, { gameId?: string } | void>({
      query: (args) => ({
        url: QUEUE_ROUTES.list,
        params: args && args.gameId ? { gameId: args.gameId } : undefined,
      }),
      transformResponse: (response: unknown) => queueListOf(unwrapData<unknown>(response)),
      providesTags: ['LimuloQueue'],
    }),

    /**
     * `tournament.queue.join` — in un modello (`queueId`) o in tutti quelli di un gioco
     * (`gameId` + `anyMode`). Entrare di nuovo aggiunge code e rinnova, senza perdere l'anzianita'.
     * Il corpo (e perche' `profileId` e non `profile`): `queueJoinBody`.
     */
    postLimuloQueueJoin: builder.mutation<LimuloQueueTicket | null, QueueJoinInput>({
      query: (input) => ({ url: QUEUE_ROUTES.join, method: 'POST', body: queueJoinBody(input) }),
      transformResponse: (response: unknown) => ticketOf(unwrapData<unknown>(response)),
      invalidatesTags: ['LimuloQueue'],
    }),

    /**
     * `tournament.queue.leave` — da un modello, o senza `queueId` da tutte le code del gioco.
     * ⚠️ `queue.not-waiting` qui vuol dire «eri gia' fuori»: v. `leftAlready`.
     */
    postLimuloQueueLeave: builder.mutation<LimuloQueueTicket | null, { gameId: string; queueId?: string | null }>({
      query: (input) => ({ url: QUEUE_ROUTES.leave, method: 'POST', body: queueLeaveBody(input) }),
      transformResponse: (response: unknown) => ticketOf(unwrapData<unknown>(response)),
      invalidatesTags: ['LimuloQueue'],
    }),

    /**
     * `tournament.queue.renew` — il biglietto del gioco, SEMPRE (anche chiuso: dice perche' e
     * dove andare). La chiama solo `startRenewLoop` (`lib/limulo/rinnovoCoda.ts`), in primo piano.
     * ⛔ Non invalida la lista: un rinnovo ogni 20 s non deve trascinarsi una rilettura.
     */
    postLimuloQueueRenew: builder.mutation<LimuloQueueTicket | null, { gameId: string }>({
      query: ({ gameId }) => ({ url: QUEUE_ROUTES.renew, method: 'POST', body: { gameId: gameId.trim() } }),
      transformResponse: (response: unknown) => ticketOf(unwrapData<unknown>(response)),
    }),

    /**
     * `chess.session.ticket.issue` — il ticket MONOUSO (30 s) per aprire il socket del tavolo degli scacchi
     * (ADR-0193 §3.5): `POST /v1/games/chess/session-ticket` con `{ purpose: "match:<id>" }`. Rende `{ ticket, wsUrl }`,
     * o `{ assente: 'ticket' | 'wsUrl' }` se la risposta non ha l'una o l'altro (e' `TICKET_UNAVAILABLE` per chi lo chiama;
     * I-545: l'assenza dice QUALE dei due manca, per la riga di diagnosi — mai il valore).
     *
     * 🔴 Una MUTATION e non una query, apposta: una query la cache la serve di nuovo, e un ticket speso rigiocato e'
     * `INVALID_TICKET`. E non si ritenta: `limuloBaseQuery` non ritenta nessuna POST (`retryCondition`), e la riconnessione
     * la fa il client del pacchetto, che chiede un ticket NUOVO a ogni tentativo (`hooks/useChessTavolo.ts`).
     * ⛔ Il ticket non si scrive in nessun log, e non entra in nessuna cache: chi chiama lo consuma subito.
     */
    issueChessTicket: builder.mutation<ChessTicketGrant | ChessTicketAssente, { matchId: string }>({
      query: ({ matchId }) => ({ url: CHESS_TICKET_ROUTE, method: 'POST', body: chessTicketBody(matchId) }),
      transformResponse: (response: unknown) => chessTicketRispostaOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /**
     * `chess.practice.create` — la partita contro il BOT degli scacchi (E7 di ADR-0193; Luca, I-561/I-562):
     * `POST /v1/games/chess/practice-matches` con `{ botElo, mode, color }`. Rende `{ ok: true, pratica }` oppure `{ ok: false, campo }`
     * col nome del campo della risposta che manca o e' strano (`lib/chess/pratica.ts`: la forma sta LI', in un posto solo).
     * La busta e' quella di I-545 (`body.data.data`): si legge con `capabilityPayloadOf(unwrapData(...))`, mai a mano.
     *
     * 🔴 Una MUTATION e non una query, apposta: e' una POST che CREA una partita (o riprende quella viva, `resumed: true`). Non si
     * ritenta (`limuloBaseQuery` non ritenta nessuna POST) e non si mette in cache. Il corpo lo valida chi chiama (`praticaBodyOf`):
     * un'Elo fuori scala non esce dal telefono.
     */
    createChessPractice: builder.mutation<PraticaRisposta, SceltePratica>({
      query: (scelte) => ({ url: PRATICA_ROUTE, method: 'POST', body: scelte }),
      transformResponse: (response: unknown) => praticaRispostaOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /**
     * `burraco.session.ticket.issue` — il ticket MONOUSO (30 s) per aprire il socket del tavolo di burraco (ADR-0108 §3 (b),
     * ADR-0124): `POST /v1/games/burraco/session-ticket` con `{ purpose: "table:<tableId>" }`. Rende `{ ticket, wsUrl }`, o `null`
     * se la risposta non ha l'una o l'altro. Forma copiata da QBurraco (`lib/burraco/ticket.ts`, con la sha).
     * 🔴 Una MUTATION, come quella degli scacchi: una query la cache la serve di nuovo, e un ticket speso rigiocato e' rifiutato.
     * Non si ritenta (nessuna POST si ritenta) e non si salva: chi la chiama la consuma subito (`lib/burraco/useTavolo.ts`).
     */
    issueBurracoTicket: builder.mutation<BurracoTicketGrant | null, { tableId: string }>({
      query: ({ tableId }) => ({ url: BURRACO_TICKET_ROUTE, method: 'POST', body: burracoTicketBody(tableId) }),
      transformResponse: (response: unknown) => burracoTicketGrantOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /**
     * `burraco.table.mine.list` — «I miei tavoli / Riprendi» (ingresso al Burraco, PR1): i tavoli di burraco ancora VIVI di chi chiama, `GET
     * /v1/games/burraco/tables/mine`. Una LETTURA (si ritenta come ogni lettura), mai in cache oltre la schermata (`keepUnusedDataFor: 0`): un tavolo che
     * non c'e piu e un «Riprendi» che porta nel vuoto. La forma e quella MISURATA da tma-orcb (12/10): DUE livelli di `data`, la lista in `body.data.data`
     * (`lib/burraco/tavoliMiei.ts`). Il codice d'errore sta in `code` (`E_QLASH_CAPABILITY_DISABLED` col 424 = «non disponibile»). Il solo consumatore e
     * `components/burraco/TavoliMiei.tsx`.
     */
    getMyBurracoTables: builder.query<BurracoTavoliMiei, void>({
      query: () => ({ url: BURRACO_TAVOLI_MIEI_ROUTE }),
      transformResponse: (response: unknown) => burracoTavoliMieiOf(response),
      keepUnusedDataFor: 0,
    }),

    /**
     * `burraco.condotta.read` — la PROPRIA condotta al tavolo (I-762, il messaggio di SOSPENSIONE): `GET /v1/games/burraco/condotta/mine`. Una LETTURA, mai in cache oltre
     * la chiamata (`keepUnusedDataFor: 0`: una sospensione che e finita o e appena cominciata non si serve dalla memoria). La chiede UN solo consumatore, DOPO il 403 di un tavolo
     * `casual` (`components/burraco/GiocaBurraco.tsx`, hook lazy): il BFF non fa arrivare il motivo del 403, quindi si rilegge e si dice cio' che il server HA. Rende la condotta
     * o `null` se la forma non si legge (`lib/burraco/condotta.ts`, lettura CHIUSA). ⚠️ Che stg la serva NON e misurato: letto dal catalogo di qlash-limulo.
     */
    getMyBurracoCondotta: builder.query<CondottaLetta | null, void>({
      query: () => ({ url: BURRACO_CONDOTTA_ROUTE }),
      transformResponse: (response: unknown) => burracoCondottaOf(capabilityPayloadOf(unwrapData<unknown>(response))),
      keepUnusedDataFor: 0,
    }),

    /**
     * `burraco.table.create` — «Gioca» e «Invita un amico» (ingresso al Burraco, PR2): crea un tavolo, `POST /v1/games/burraco/tables` con `{ tipo, posti, idempotencyKey }`; rende il
     * tavolo (`tableId`, e per un privato l'`invito`, reso UNA volta) o `null` se la risposta non ha la forma. Forma presa dal CODICE del resolver (`tableCreate`, QBurraco @ 83ab2d23), busta
     * di un game host: si scende con `capabilityPayloadOf`. ⚠️ IPOTESI: nessuno ha ancora misurato una POST (Z8).
     * 🔴 Una MUTATION che CREA un tavolo: non si ritenta (`limuloBaseQuery` non ritenta nessuna POST) e non si mette in cache; il doppio tocco lo ferma `useAzioneBurraco` e la chiave di
     * idempotenza nel corpo (`idempotencyKey`, la stessa a ogni ripetizione della STESSA azione). Non manda MAI `classificato`, `fascia`, `posto` ne il livello dei bot (rifiutati dal resolver).
     */
    createBurracoTable: builder.mutation<TavoloCreato | null, { tipo: TipoTavolo; posti: number; idempotencyKey: string }>({
      query: (a) => ({ url: BURRACO_TAVOLI_ROUTE, method: 'POST', body: creaTavoloBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /**
     * `burraco.table.join` — «Entra con un codice» (PR2): `POST /v1/games/burraco/tables/:tableId/seats` con `{ invito, idempotencyKey }`; il posto lo sceglie il game host. Rende
     * l'ingresso o `null` se la risposta non ha un `posto`. ⚠️ IPOTESI (Z8), come `createBurracoTable`. «Non c'e», «finito» e «invito sbagliato» sono UNA risposta (404) di proposito.
     * 🔴 Una MUTATION: non si ritenta e non si mette in cache.
     */
    joinBurracoTable: builder.mutation<IngressoRiuscito | null, { tableId: string; invito: string; idempotencyKey: string }>({
      query: (a) => ({ url: burracoPostiRoute(a.tableId), method: 'POST', body: entraBody(a) }),
      transformResponse: (response: unknown, _meta, a) => ingressoOf(capabilityPayloadOf(unwrapData<unknown>(response)), a.tableId),
    }),

    /**
     * `chinesepoker.session.ticket.issue` — il ticket MONOUSO (30 s) per aprire il socket di QChinesePoker (T1a; U4 di tma-orcb):
     * `POST /v1/games/chinesepoker/session-ticket` con `{ purpose: "queue" | "invite" | "match:<uuid>" }`. Rende `{ ticket, wsUrl }`, o
     * `null` se la risposta non ha l'uno o l'altro. Forma presa dal codice del resolver (contratto U0), busta `body.data.data`.
     * 🔴 Una MUTATION, come le gemelle: una query la cache la serve di nuovo, e un ticket speso rigiocato e' `invalid_ticket`. Non si
     * ritenta (nessuna POST si ritenta) e non si salva: chi la chiama la consuma subito e fa `reset()` (`hooks/useQcpIssueTicket.ts`).
     * Il 4xx del resolver arriva appiattito in `E_QLASH_HTTP_<status>`, ogni guasto in `424 E_QLASH_CAPABILITY_RESOLVER_ERROR`.
     */
    issueChinesePokerTicket: builder.mutation<QcpTicketGrant | null, { purpose: QcpTicketPurpose }>({
      query: ({ purpose }) => ({ url: QCP_TICKET_ROUTE, method: 'POST', body: qcpTicketBody(purpose) }),
      transformResponse: (response: unknown) => qcpTicketGrantOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /**
     * QChinesePoker dal BFF (T1b; condizioni di tma-orcb U0-U6): il REST che la TMA faceva a QCP col Bearer Logto passa da qui, sulla
     * sessione di Limulo, come le altre capability. `/v1/games/chinesepoker/*`, forma presa dal codice del resolver (`lib/qcp/rest.ts`):
     * doppia busta `data.data`, liste incluse (per le liste `capabilityPayloadOf` NON scende: `qcpListOf`). Un corpo che non ha la forma
     * attesa e' `null` (errore con «Riprova»), mai un elenco vuoto. Letture in cache poca (`keepUnusedDataFor`), nessun tag.
     */
    /**
     * `chinesepoker.me.read` — «chi sono io» per QCP (I-646, X2-X3 di tma-orcb): `userId` e' lo `User.id` delle righe di QCP, NON il `sub`
     * Logto. Accessorio: un suo guasto (424, rete, forma rotta) non e' mai un errore di schermata, il consumatore (`useQcpMe`) lo legge
     * come «non lo so». Nessun header, nessun tag; l'identita' non cambia nella sessione (il logout ricarica la pagina): cache lunga.
     */
    getChinesePokerMe: builder.query<QcpMe | null, void>({
      query: () => ({ url: QCP_REST.me }),
      transformResponse: (response: unknown) => qcpMeOf(capabilityPayloadOf(unwrapData<unknown>(response))),
      keepUnusedDataFor: 86_400,
    }),
    getChinesePokerLeaderboard: builder.query<LeaderboardEntry[] | null, { limit?: number }>({
      query: ({ limit = 50 } = {}) => ({ url: QCP_REST.leaderboard, params: { limit } }),
      transformResponse: (response: unknown) => qcpLeaderboardOf(unwrapData<unknown>(response)),
      keepUnusedDataFor: 60,
    }),
    getChinesePokerMyMatches: builder.query<MatchSummary[] | null, { limit?: number }>({
      query: ({ limit = 20 } = {}) => ({ url: QCP_REST.myMatches, params: { limit } }),
      transformResponse: (response: unknown) => qcpMyMatchesOf(unwrapData<unknown>(response)),
      keepUnusedDataFor: 0,
    }),
    /** ⚠️ Non e' piu' pubblica: il BFF risponde solo a chi ha giocato quella partita (403 altrimenti). */
    getChinesePokerMatch: builder.query<MatchSummary | null, string>({
      query: (matchId) => ({ url: QCP_REST.match(matchId) }),
      transformResponse: (response: unknown) => qcpMatchOf(unwrapData<unknown>(response)),
      keepUnusedDataFor: 0,
    }),
    getChinesePokerReplay: builder.query<MatchReplay | null, string>({
      query: (matchId) => ({ url: QCP_REST.replay(matchId) }),
      transformResponse: (response: unknown) => qcpReplayOf(unwrapData<unknown>(response)),
      keepUnusedDataFor: 0,
    }),
    /** `invite.create` — la `idempotencyKey` (8..200) e' OBBLIGATORIA nel CORPO: la sceglie chi chiama, una per azione dell'utente. */
    createChinesePokerInvite: builder.mutation<QcpInvite | null, { idempotencyKey: string }>({
      query: ({ idempotencyKey }) => ({ url: QCP_REST.invites, method: 'POST', body: { idempotencyKey } }),
      transformResponse: (response: unknown) => qcpInviteOf(unwrapData<unknown>(response)),
    }),
    /** `invite.read` — l'anteprima di un codice; una LETTURA, ma mai in cache (un invito si consuma). */
    getChinesePokerInvite: builder.query<QcpInvitePreview | null, string>({
      query: (code) => ({ url: QCP_REST.invite(code) }),
      transformResponse: (response: unknown) => qcpInvitePreviewOf(unwrapData<unknown>(response)),
      keepUnusedDataFor: 0,
    }),
    acceptChinesePokerInvite: builder.mutation<QcpInviteAccepted | null, string>({
      query: (code) => ({ url: QCP_REST.accept(code), method: 'POST', body: {} }),
      transformResponse: (response: unknown) => qcpAcceptedOf(unwrapData<unknown>(response)),
    }),
    revokeChinesePokerInvite: builder.mutation<unknown, string>({
      query: (code) => ({ url: QCP_REST.invite(code), method: 'DELETE' }),
    }),

    /**
     * `bridge.session.ticket.issue` — il ticket MONOUSO (30 s) per aprire il socket del tavolo di bridge (K9b-1a; ADR-0108 §3 (b), ADR-0124):
     * `POST /v1/games/bridge/session-ticket` con `{ tableId }`. Rende `{ ticket, wsUrl }`, o `{ assente: 'ticket' | 'wsUrl' }` se la risposta non ha
     * l'uno o l'altro (la riga di diagnosi dice QUALE manca, mai il valore). Forma copiata da QBridge `limulo-app/` (`lib/ticket.ts`).
     * 🔴 Una MUTATION, mai una query: una query la cache la serve di nuovo, e un ticket speso rigiocato e' rifiutato. Non si ritenta (nessuna POST si
     * ritenta) e non si salva: chi la chiama la consuma subito (`lib/bridge/useTavoloVivo.ts`).
     */
    issueBridgeTicket: builder.mutation<BridgeTicketGrant | BridgeTicketAssente, { tableId: string }>({
      query: ({ tableId }) => ({ url: BRIDGE_TICKET_ROUTE, method: 'POST', body: bridgeTicketBody(tableId) }),
      transformResponse: (response: unknown) => bridgeTicketRispostaOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /**
     * `bridge.practice.create` — un tavolo di PRATICA: l'utente a Sud e tre robot dello stesso livello (K9b-1a). `POST /v1/games/bridge/practice-tables`
     * con `{ livello, clientSeed }`: il `clientSeed` e' la parte del CLIENT nel provably-fair (ADR-0214), casuale e nostro, e la risposta deve
     * restituirlo identico (`bridgePraticaRispostaOf`: altrimenti `{ ok: false, campo }` e il tavolo non si apre).
     * 🔴 Una MUTATION che CREA un tavolo (il game host ne tiene al massimo 3 aperti per persona): non si ritenta e non si mette in cache.
     */
    createBridgePractice: builder.mutation<BridgePraticaRisposta, BridgePraticaScelte>({
      query: (scelte) => ({ url: BRIDGE_PRATICA_ROUTE, method: 'POST', body: bridgePraticaBody(scelte) }),
      transformResponse: (response: unknown, _meta, scelte) => bridgePraticaRispostaOf(capabilityPayloadOf(unwrapData<unknown>(response)), scelte.clientSeed),
    }),

    /**
     * `bridge.table.mine.list` — «Riprendi» (K9b-1b-i): i tavoli di bridge ancora VIVI di chi chiama, `GET /v1/games/bridge/tables/mine`. Una LETTURA
     * (si ritenta come ogni lettura), mai in cache oltre la schermata (`keepUnusedDataFor: 0`): un tavolo vive ~10' e un elenco di prima porterebbe a
     * un tavolo che non c'e piu. Il solo consumatore e `lib/bridge/useTavoliMiei.ts`.
     */
    getMyBridgeTables: builder.query<BridgeTavoliMiei, void>({
      query: () => ({ url: BRIDGE_TAVOLI_MIEI_ROUTE }),
      transformResponse: (response: unknown) => bridgeTavoliMieiOf(capabilityPayloadOf(unwrapData<unknown>(response))),
      keepUnusedDataFor: 0,
    }),

    /**
     * `bridge.daily.today.read` per `mano` — la MANO DEL GIORNO (tma-orcb-60, M1): `GET /v1/games/bridge/daily/mano/today`. Una LETTURA, mai in cache oltre la schermata
     * (`keepUnusedDataFor: 0`: il giorno cambia a mezzanotte di Roma). Rende la mano o `null` se la forma non e' quella misurata (`bridgeManoOf`, lettura CHIUSA).
     */
    getBridgeManoDelGiorno: builder.query<ManoDelGiorno | null, void>({
      query: () => ({ url: BRIDGE_MANO_TODAY_ROUTE }),
      transformResponse: (response: unknown) => bridgeManoOf(capabilityPayloadOf(unwrapData<unknown>(response))),
      keepUnusedDataFor: 0,
    }),

    /**
     * `bridge.daily.next-board` per `mano` — «Gioca» (M3): `POST /v1/games/bridge/daily/mano/next-board` con l'`idempotencyKey` DEL TOCCO nel corpo (il mirror la gira all'header).
     * Rende `{ tableId }` o `null` (2xx senza un tavolo che si legge). 🔴 Una MUTATION: iscrive alla mano, non si ritenta da sola.
     */
    playBridgeManoDelGiorno: builder.mutation<{ tableId: string } | null, { idempotencyKey: string }>({
      query: ({ idempotencyKey }) => ({ url: BRIDGE_MANO_NEXT_BOARD_ROUTE, method: 'POST', body: { idempotencyKey } }),
      transformResponse: (response: unknown) => bridgeNextBoardOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /** `bridge.daily.today.read` per `quotidiano` (tma-orcb-61, D1): come la mano, un'altra rotta e un altro parser (CHIUSO: `dq-`, `kind: "daily"`). */
    getBridgeQuotidiano: builder.query<QuotidianoDelGiorno | null, void>({
      query: () => ({ url: BRIDGE_QUOTIDIANO_TODAY_ROUTE }),
      transformResponse: (response: unknown) => bridgeQuotidianoOf(capabilityPayloadOf(unwrapData<unknown>(response))),
      keepUnusedDataFor: 0,
    }),

    /** `bridge.daily.next-board` per `quotidiano` (D3): gioca, continua e riprendi, con l'`idempotencyKey` DEL TOCCO nel corpo. Una MUTATION, non si ritenta da sola. */
    playBridgeQuotidiano: builder.mutation<{ tableId: string } | null, { idempotencyKey: string }>({
      query: ({ idempotencyKey }) => ({ url: BRIDGE_QUOTIDIANO_NEXT_BOARD_ROUTE, method: 'POST', body: { idempotencyKey } }),
      transformResponse: (response: unknown) => bridgeNextBoardOf(capabilityPayloadOf(unwrapData<unknown>(response))),
    }),

    /**
     * `GET /v1/me/wallet` — il saldo di chi chiama, dalla facciata (D-WALLET, `qbank:wallets.mine.balances`).
     * Il SOLO consumatore e' `hooks/useWallets.ts`, a `VITE_FEATURE_WALLET_FACCIATA_ENABLED` acceso
     * (L-2717 (a)): restituisce lo STESSO `Wallet[]` di `qbankApi.getWallets`, mappato in `lib/limulo/wallet.ts`.
     *
     * ⛔ NESSUNA query, mai `sub` ne' `userId`: il soggetto lo ricava la facciata dalla sessione, e un'identita'
     * in query non viene nemmeno inoltrata. Una pagina sola, `Cache-Control: private, no-store`.
     * ⚠️ Con `enableWallet` spento (o senza la capability) la facciata risponde con un errore (403/404/424): resta
     * un ERRORE, che la HUD legge come «saldo non disponibile» e nasconde — mai uno `[]` che diventi «0».
     */
    getLimuloMyWallet: builder.query<Wallet[], void>({
      query: () => '/v1/me/wallet',
      transformResponse: (response: unknown) => walletsOf(response),
      providesTags: [LIMULO_WALLET_TAG],
      // PARITA' DI CACHE con `qbankApi` (120 s, contro i 60 del resto di Limulo): il saldo che la testata mostra
      // non deve cambiare ritmo di rilettura perche' ha cambiato fonte. `refetchOnFocus: false` sta nella
      // sottoscrizione di `useWallets` (un'opzione di endpoint non c'e'): `limuloApi` lo ha `true` per tutto il resto.
      keepUnusedDataFor: 120,
    }),
  }),
});

export const {
  useCreateChessPracticeMutation,
  useGetMyBridgeTablesQuery,
  useGetBridgeManoDelGiornoQuery,
  usePlayBridgeManoDelGiornoMutation,
  useGetBridgeQuotidianoQuery,
  usePlayBridgeQuotidianoMutation,
  useGetMyBurracoTablesQuery,
  useLazyGetMyBurracoCondottaQuery,
  useCreateBurracoTableMutation,
  useJoinBurracoTableMutation,
  useGetLimuloTournamentsQuery,
  useGetMyLimuloTournamentsQuery,
  useGetMyLimuloTournamentsConTotaleQuery,
  useGetLimuloAzioniQuery,
  useGetLimuloTournamentParticipantsQuery,
  useGetLimuloTournamentHistoryQuery,
  useGetLimuloTournamentQuery,
  useGetLimuloRegistrationQuery,
  useGetLimuloRegistrationRequirementsQuery,
  useGetLimuloStandingsQuery,
  useGetLimuloMeQuery,
  useGetLimuloWalletCurrenciesQuery,
  usePutLimuloNicknameMutation,
  // Fase 3 — nessuna schermata li usa ancora: il cablaggio e' il giro successivo.
  usePostLimuloRegistrationMutation,
  useDeleteLimuloRegistrationMutation,
  usePostLimuloTournamentCheckinMutation,
  useGetLimuloMatchesQuery,
  useGetLimuloPartiteInCorsoQuery,
  useGetLimuloMatchQuery,
  useGetLimuloMatchGameIdsQuery,
  useGetLimuloMatchResultQuery,
  // ⚠️ Le due varianti LAZY servono alla RILETTURA dopo un rifiuto di scrittura, non a una
  // schermata: il BFF appiattisce ogni rifiuto nominato del provider in un 424 muto (v. il
  // cappello di `lib/limulo/matchActions.ts`), e l'unico modo di sapere com'e' andata e'
  // guardare cosa il server HA. Deve partire SU RICHIESTA, dopo il rifiuto — un `useQuery`
  // al montaggio leggerebbe lo stato di prima.
  useLazyGetLimuloMatchQuery,
  useLazyGetLimuloMatchResultQuery,
  usePostLimuloMatchResultMutation,
  usePostLimuloMatchResultMediaMutation,
  usePostLimuloMatchCheckinMutation,
  usePostLimuloMatchNotReadyMutation,
  usePostLimuloMatchForfeitMutation,
  // La chat dell'incontro (eFootball T3): la usa solo `LimuloMatchChat`.
  useGetLimuloMatchChatQuery,
  useGetLimuloMatchMessagesQuery,
  usePostLimuloMatchMessageMutation,
  useGetLimuloGamesQuery,
  useGetLimuloGameMatchModesQuery,
  useGetLimuloPlatformsQuery,
  useGetLimuloFavoriteGamesQuery,
  useGetLimuloGameProfileRequirementsQuery,
  // Lo schema letto AL TAP, agganciandosi alla lettura in volo (`preferCacheValue`): l'«Entra» di un sit&go con un
  // profilo gia' scelto non mostra niente mentre lo schema arriva, e lo aspetta solo se serve (I-349).
  useLazyGetLimuloGameProfileRequirementsQuery,
  useGetLimuloProfilesQuery,
  // La rilettura dopo un rifiuto di scrittura, stessa ragione delle due lazy qui sopra.
  useLazyGetLimuloProfilesQuery,
  useLazyGetLimuloRegistrationQuery,
  useGetLimuloProfileQuery,
  useCreateLimuloProfileMutation,
  useUpdateLimuloProfileMutation,
  usePostLimuloProfileSyncMutation,
  usePostLimuloLogoutMutation,
  // Le code (kb#282): dichiarate da qlash-limulo#146, servite da qlash-limulo#151; le usa la «Ranked».
  useGetLimuloQueuesQuery,
  usePostLimuloQueueJoinMutation,
  usePostLimuloQueueLeaveMutation,
  usePostLimuloQueueRenewMutation,
  // La sfida fra amici (I-207, T2): l'invio usa solo la mutazione di creazione; le altre
  // (lista, lettura, accetta/rifiuta/ritira) sono per il lotto B — vivono qui perche' il client
  // e' uno per servizio.
  useCreateLimuloChallengeMutation,
  useGetLimuloChallengesQuery,
  useGetLimuloChallengeQuery,
  useAcceptLimuloChallengeMutation,
  useDeclineLimuloChallengeMutation,
  useRevokeLimuloChallengeMutation,
  // Il saldo dalla facciata (L-2717 (a)): lo chiama solo `useWallets`.
  useGetLimuloMyWalletQuery,
  // QChinesePoker dal BFF (T1b): le chiama solo i pannelli e i fogli di `components/chinesepoker`, `hub/panels/Qcp*`, `routes/chinesepoker`.
  useGetChinesePokerMeQuery,
  useGetChinesePokerLeaderboardQuery,
  useGetChinesePokerMyMatchesQuery,
  useGetChinesePokerMatchQuery,
  useGetChinesePokerReplayQuery,
  useLazyGetChinesePokerInviteQuery,
  useCreateChinesePokerInviteMutation,
  useAcceptChinesePokerInviteMutation,
  useRevokeChinesePokerInviteMutation,
} = limuloApi;
